import {
  ShieldCheck,
  Car,
  HeartPulse,
  Home,
  Sofa,
  Dog,
  Scale,
  Activity,
  Smile,
  Stethoscope,
  Briefcase,
  Building2,
  Cpu,
  Server,
  Wrench,
  Truck,
  Users,
  ShieldAlert,
  Lock,
  CreditCard,
  Landmark,
  TrendingUp,
  PiggyBank,
  Coins,
  Accessibility,
  HeartHandshake,
  Hourglass,
  BadgeCheck,
  LineChart,
  HandHeart,
  Plane,
  Bike,
  AlertOctagon,
  type LucideIcon,
} from "lucide-react";

export type ServiceCategoryName =
  | "Einkommensversicherung"
  | "Sachversicherungen"
  | "Gesundheitsvorsorge"
  | "Gewerbeversicherung"
  | "Finanzierung"
  | "Altersvorsorge & Vermögensaufbau";

export type ServiceSection = {
  heading: string;
  intro?: string;
  bullets?: { title: string; text: string }[];
  paragraphs?: string[];
};

export type Service = {
  slug: string;
  name: string;
  shortName: string;
  category: ServiceCategoryName;
  icon: LucideIcon;
  tagline: string;
  intro: string;
  forWhom: string;
  highlights: string[];
  sections: ServiceSection[];
  notCovered: string[];
  conclusion: string;
  badge?: string;
  /** Optional authored SEO title (~55–60 chars incl. " | FINABLE"). Falls back to `${shortName} – ${tagline} | FINABLE`. */
  metaTitle?: string;
  /** Optional authored meta description (~150–155 chars, complete sentence). Falls back to `intro.slice(0, 155)`. */
  metaDescription?: string;
  /** Internal glossary slugs to surface as related terms (see src/lib/serviceLinks.ts). */
  glossaryLinks?: string[];
  /** Internal blog post slugs to surface as further reading (see src/lib/serviceLinks.ts). */
  blogLinks?: string[];
};

export const SERVICE_CATEGORIES: { name: ServiceCategoryName; description: string }[] = [
  {
    name: "Einkommensversicherung",
    description: "Schutz für Deine Arbeitskraft – das wichtigste Kapital, das Du hast.",
  },
  {
    name: "Sachversicherungen",
    description: "Schutz für Dein Zuhause, Dein Fahrzeug und Deinen Alltag.",
  },
  {
    name: "Gesundheitsvorsorge",
    description: "Bestmögliche medizinische Versorgung – privat oder als Ergänzung.",
  },
  {
    name: "Altersvorsorge & Vermögensaufbau",
    description: "Steuerlich gefördert sparen, langfristig Vermögen aufbauen.",
  },
  {
    name: "Gewerbeversicherung",
    description: "Maßgeschneiderte Lösungen für Selbstständige und Unternehmen.",
  },
  {
    name: "Finanzierung",
    description: "Konsumkredit oder Immobilienfinanzierung – wir finden die besten Konditionen.",
  },
];

// Reusable factory for compact entries to keep the file readable
const make = (
  s: Omit<Service, "sections" | "notCovered"> & {
    sections?: ServiceSection[];
    notCovered?: string[];
  },
): Service => ({
  sections: s.sections ?? [],
  notCovered: s.notCovered ?? [],
  ...s,
});

export const SERVICES: Service[] = [
  // ─────────────── Einkommensversicherung ───────────────
  make({
    slug: "berufsunfaehigkeitsversicherung",
    metaTitle: "Berufsunfähigkeitsversicherung vergleichen | FINABLE",
    metaDescription: "Berufsunfähigkeitsversicherung mit fairen Bedingungen: Klauseln prüfen, Anbieter unabhängig vergleichen und Deine Arbeitskraft passgenau absichern.",
    name: "Berufsunfähigkeits­versicherung",
    shortName: "Berufsunfähigkeit",
    category: "Einkommensversicherung",
    icon: Briefcase,
    tagline: "Die wichtigste Absicherung Deiner Arbeitskraft.",
    intro:
      "Jede:r vierte Erwerbstätige in Deutschland wird im Laufe des Berufslebens berufsunfähig – meist durch psychische Erkrankungen, Rückenleiden oder Krebs. Die gesetzliche Erwerbsminderungsrente reicht nicht zum Leben. Die Berufsunfähigkeitsversicherung zahlt eine vereinbarte monatliche Rente, sobald Du Deinen Beruf zu mindestens 50 % nicht mehr ausüben kannst.",
    forWhom:
      "Für jede:n Berufstätige:n. Je früher abgeschlossen, desto günstiger und gesünder bist Du noch – ideal schon als Azubi, Student:in oder Berufsstarter:in.",
    highlights: [
      "Monatliche Rente bei 50 % Berufsunfähigkeit",
      "Verzicht auf abstrakte Verweisung",
      "Nachversicherungsgarantie ohne Gesundheitsprüfung",
      "Beitragsdynamik und Rentendynamik möglich",
    ],
    sections: [
      {
        heading: "Worauf es bei einem Top-Tarif ankommt",
        bullets: [
          { title: "Verzicht auf abstrakte Verweisung", text: "Die Leistung greift, wenn Du in Deinem zuletzt ausgeübten Beruf nicht mehr arbeiten kannst – der Versicherer darf Dich nicht auf einen anderen, theoretischen Beruf verweisen." },
          { title: "Prognosezeitraum von 6 Monaten", text: "Die Rente fließt, sobald ein Arzt prognostiziert, dass Du voraussichtlich länger als sechs Monate berufsunfähig bist – nicht erst nach Ablauf der Frist." },
          { title: "Freie Arztwahl", text: "Du wählst den Arzt Deines Vertrauens für die Diagnose – kein vom Versicherer benannter Gutachter." },
          { title: "Richtiges Endalter", text: "Idealerweise leistet die Berufsunfähigkeitsversicherung bis zum Ende des Erwerbslebens, sodass nahtlos die gesetzliche Altersrente anschließt." },
          { title: "Leistung bei grober Fahrlässigkeit", text: "Damit gibt es keinen Streit über die Abgrenzung zwischen Fahrlässigkeit und grober Fahrlässigkeit – z. B. bei Verkehrsdelikten." },
          { title: "Arbeitsunfähigkeitsklausel (AU-Klausel)", text: "Bist Du länger als sechs Monate arbeitsunfähig, bekommst Du auch ohne nachgewiesene Berufsunfähigkeit die volle Berufsunfähigkeitsrente." },
          { title: "Nachversicherungsgarantie", text: "Heirat, Geburt, Hauskauf, Gehaltssprung – die Rente lässt sich ohne erneute Gesundheitsprüfung erhöhen." },
          { title: "Beitrags- und Rentendynamik", text: "Schützt Dich vor Inflation – Beitrag und Rente steigen jährlich automatisch." },
        ],
      },
      {
        heading: "Was passiert ohne Berufsunfähigkeitsversicherung?",
        paragraphs: [
          "Im Krankheitsfall zahlt der Arbeitgeber die ersten sechs Wochen den vollen Lohn. Danach übernimmt die Krankenkasse mit dem Krankengeld – meist nur rund 70 % des Bruttoeinkommens, gedeckelt auf maximal 78 Wochen.",
          "Reicht es danach immer noch nicht zur Berufsausübung, greift die gesetzliche Erwerbsminderungsrente. Diese liegt im Schnitt unter 1.000 € im Monat – und nur, wenn Du komplett erwerbsunfähig bist. Wer seinen Beruf nicht mehr ausüben kann, geht oft komplett leer aus.",
        ],
      },
      {
        heading: "Warum so früh wie möglich abschließen?",
        bullets: [
          { title: "Gesundheitszustand", text: "Je jünger und gesünder Du bist, desto weniger Ausschlüsse oder Risikozuschläge gibt es bei der Annahme." },
          { title: "Niedrigere Beiträge", text: "Eintrittsalter spielt die größte Rolle bei der Beitragshöhe – je früher Du einsteigst, desto günstiger sind die Konditionen für die gesamte Laufzeit." },
          { title: "Bereits als Schüler:in / Azubi möglich", text: "Bereits als Schüler:in oder Azubi abschließbar – gerade auch für Risikoberufe ein Muss. Es gibt auch Versicherer, die Kinder-Berufsunfähigkeitsversicherung versichern." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzlich herbeigeführte Berufsunfähigkeit",
      "Verschwiegene Vorerkrankungen aus dem Antrag",
      "Berufsunfähigkeit infolge von Kriegsereignissen oder innerer Unruhen",
    ],
    conclusion:
      "Die Berufsunfähigkeitsversicherung ist komplex – kleine Klauseln entscheiden im Ernstfall über fünf- oder sechsstellige Summen. Wir analysieren Deinen Beruf, prüfen Vorerkrankungen anonym vorab und finden den Tarif mit der besten Bedingungs­qualität.",
  }),

  make({
    slug: "grundfaehigkeitenversicherung",
    metaTitle: "Grundfähigkeitenversicherung – BU-Alternative | FINABLE",
    metaDescription: "Grundfähigkeitenversicherung als BU-Alternative: Absicherung, wenn wichtige Fähigkeiten wie Sehen, Gehen oder Greifen dauerhaft verloren gehen.",
    name: "Grundfähigkeiten­versicherung",
    shortName: "Grundfähigkeiten",
    category: "Einkommensversicherung",
    icon: Activity,
    tagline: "Alternative oder Ergänzung zur Berufsunfähigkeitsversicherung.",
    intro:
      "Wenn Dir der Verlust grundlegender Fähigkeiten wie Sehen, Hören, Gehen oder die Nutzung der Hände droht, zahlt die Grundfähigkeiten­versicherung eine vereinbarte Rente – unabhängig vom Beruf. Besonders interessant für handwerkliche Berufe oder bei Vorerkrankungen, die eine Berufsunfähigkeitsversicherung verteuern.",
    forWhom:
      "Für Handwerker:innen, körperlich Tätige, Schüler:innen sowie alle, die in der Berufsunfähigkeitsversicherung schwer oder nur teuer versicherbar sind.",
    highlights: [
      "Leistung beim Verlust definierter Grundfähigkeiten",
      "Günstiger als Berufsunfähigkeitsversicherung bei körperlichen Berufen",
      "Einfachere Gesundheitsprüfung",
      "Sinnvolle Ergänzung zur Berufsunfähigkeitsversicherung",
    ],
    sections: [
      {
        heading: "So unterscheidet sie sich von der Berufsunfähigkeitsversicherung",
        paragraphs: [
          "Während die Berufsunfähigkeitsversicherung den Verlust Deiner konkreten Berufstätigkeit absichert, leistet die Grundfähigkeitenversicherung beim Verlust definierter körperlicher oder geistiger Fähigkeiten – unabhängig davon, ob Du Deinen Beruf trotzdem noch ausüben könntest.",
          "Die Leistungen sind in der Regel niedriger als bei einer Berufsunfähigkeitsversicherung, dafür sind die Beiträge wesentlich günstiger. So können sich auch Personen in Risikoberufen wie Handwerker:innen oder Pflegekräfte einen soliden Arbeitskraftschutz leisten.",
        ],
      },
      {
        heading: "Versicherte Grundfähigkeiten (Auswahl)",
        bullets: [
          { title: "Sehen & Hören", text: "Verlust eines Sinnesorgans wird zuverlässig anerkannt." },
          { title: "Sprechen", text: "Wichtig für Lehrer:innen, Berater:innen und alle kommunikativen Berufe." },
          { title: "Hände gebrauchen", text: "Greifen, Halten, Tasten – essenziell für Handwerk und Pflege." },
          { title: "Gehen, Treppensteigen, Stehen", text: "Mobilität als Voraussetzung für viele Berufe." },
          { title: "Auto fahren", text: "Wichtig für Außendienst, Logistik und Pendler:innen." },
          { title: "Geistige Leistungsfähigkeit", text: "Demenz, schwere Konzentrationsstörungen oder psychische Erkrankungen." },
        ],
      },
      {
        heading: "Wann wird gezahlt?",
        paragraphs: [
          "Voraussetzung ist, dass eine oder mehrere versicherte Grundfähigkeiten – je nach Anbieter mit unterschiedlichen Definitionen – für mindestens zwölf Monate verloren gegangen sind. Dann fließt die vereinbarte monatliche Rente unabhängig von der Ursache. Auch der Pflegefall ist in der Regel mitversichert.",
        ],
      },
    ],
    notCovered: [
      "Verlust von Fähigkeiten, die nicht im Bedingungswerk genannt sind",
      "Vorsätzliche Selbstverletzung",
      "Verschwiegene Vorerkrankungen aus dem Antrag",
    ],
    conclusion:
      "Wir prüfen, ob die Grundfähigkeitenversicherung für Dich Ersatz oder Ergänzung zur Berufsunfähigkeitsversicherung ist – und welche Anbieter den besten Fähigkeitenkatalog mit klaren Definitionen bieten.",
  }),

  make({
    slug: "risikolebensversicherung",
    metaTitle: "Risikolebensversicherung – Familie absichern | FINABLE",
    metaDescription: "Risikolebensversicherung günstig abschließen: Familie und Kredite absichern, Anbieter vergleichen und passende Versicherungssumme gemeinsam festlegen.",
    name: "Risikolebens­versicherung",
    shortName: "Risikoleben",
    category: "Einkommensversicherung",
    icon: HeartHandshake,
    tagline: "Familie absichern – schon ab wenigen Euro im Monat.",
    intro:
      "Die Risikolebensversicherung zahlt im Todesfall eine vereinbarte Summe an Deine Hinterbliebenen. So bleibt die Familie finanziell abgesichert – sei es zur Tilgung der Immobilie oder zur Sicherung des Lebensstandards. Für junge gesunde Menschen oft schon ab 5–15 € im Monat erhältlich.",
    forWhom:
      "Für Familien, Paare mit gemeinsamen Verpflichtungen und alle, die eine Immobilie finanzieren.",
    highlights: [
      "Hohe Versicherungssumme zu kleinem Beitrag",
      "Verbundene Police für Paare möglich",
      "Sinnvoll bei Immobilienfinanzierung",
      "Über-Kreuz-Versicherung optimiert Erbschaftsteuer",
    ],
    sections: [
      {
        heading: "Warum brauchst Du eine Risikoleben?",
        paragraphs: [
          "Dein Einkommen bildet die Basis, um sämtliche finanziellen Verpflichtungen Deiner Familie zu stemmen – Miete oder Tilgung, Lebenshaltung, Kinderbetreuung. Verstirbt ein Partner, reicht das verbleibende Einkommen oft nicht aus.",
          "Die Risikolebensversicherung zahlt im Todesfall eine vereinbarte Einmalsumme an Deine Hinterbliebenen. Damit lassen sich Hypotheken tilgen, der Lebensstandard sichern oder Ausbildungskosten der Kinder decken.",
        ],
      },
      {
        heading: "Verbundene oder Einzelpolice?",
        paragraphs: [
          "Bei einer verbundenen Risikoleben schließen zwei Personen einen gemeinsamen Vertrag ab – der Beitrag fällt nur einmal an. Das spart Geld, ist aber nicht für jeden sinnvoll.",
        ],
        bullets: [
          { title: "Nicht ideal für Unverheiratete", text: "Der erbschaftsteuerliche Freibetrag liegt nur bei 20.000 €. Eine Einzelpolice (Über-Kreuz) ist hier oft günstiger." },
          { title: "Nicht ideal bei mehreren Kindern", text: "Die Todesfallsumme wird bei Versterben beider Partner nur einmal ausgezahlt – das kann zu wenig sein." },
          { title: "Nicht ideal bei individuellen Bedürfnissen", text: "Wenn Laufzeit oder Versicherungssumme für jeden Partner unterschiedlich sein sollen, sind Einzelverträge die bessere Wahl." },
        ],
      },
      {
        heading: "Steuerliche Aspekte",
        bullets: [
          { title: "Freibetrag Ehepartner", text: "500.000 € – die Auszahlung bleibt damit meist steuerfrei." },
          { title: "Freibetrag Kinder", text: "400.000 € pro Elternteil und Kind." },
          { title: "Freibetrag Unverheiratete", text: "Nur 20.000 € – darüber wird Erbschaftsteuer fällig. Lösung: Über-Kreuz-Versicherung." },
          { title: "Beiträge absetzbar", text: "Beiträge zählen zu den Vorsorgeaufwendungen und sind im Rahmen der Höchstgrenzen steuerlich absetzbar." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzliche Selbsttötung innerhalb der ersten drei Versicherungsjahre",
      "Tod im Zusammenhang mit verschwiegenen Vorerkrankungen",
      "Tod durch Kriegsereignisse",
    ],
    conclusion:
      "Wir berechnen die passende Versicherungssumme (Faustregel: 3–5 Brutto-Jahresgehälter plus Restschuld), vergleichen Top-Anbieter und gestalten die Police steuerlich optimal – z. B. als Über-Kreuz-Lösung für Paare.",
  }),


  // ─────────────── Sachversicherungen ───────────────
  {
    slug: "privathaftpflicht",
    metaTitle: "Privathaftpflichtversicherung vergleichen | FINABLE",
    metaDescription: "Privathaftpflicht schützt Dich vor teuren Schadenersatzforderungen: leistungsstarke Tarife vergleichen und Deine Familie zuverlässig mit absichern.",
    name: "Privathaftpflicht­versicherung",
    shortName: "Privathaftpflicht",
    category: "Sachversicherungen",
    icon: ShieldCheck,
    tagline: "Der wichtigste Schutz für Deinen Alltag.",
    intro:
      "Ein unachtsamer Moment, ein zerbrochenes Smartphone, ein Sturz auf dem nicht geräumten Gehweg – wer einen Schaden verursacht, haftet in unbegrenzter Höhe. Die Privathaftpflicht übernimmt diese Kosten und schützt Dich vor existenziellen finanziellen Folgen. Sie zählt zu den unverzichtbarsten Versicherungen überhaupt.",
    forWhom:
      "Für jeden – vom Studenten bis zur Familie. Die Beiträge sind gering, das Risiko ohne Versicherung kann Dich ein Leben lang belasten.",
    highlights: [
      "Personen-, Sach- und Vermögensschäden",
      "Weltweiter Schutz",
      "Mitversicherung von Kindern und Partner möglich",
      "Schäden durch deliktunfähige Kinder",
    ],
    sections: [
      {
        heading: "Diese Leistungen bieten Top-Tarife",
        bullets: [
          { title: "Schlüsselverlust", text: "Verlierst Du den Schlüssel zur Mietwohnung oder einen beruflich genutzten Generalschlüssel, übernimmt die Versicherung die Kosten für den Austausch der Schließanlage." },
          { title: "Gefälligkeitsschäden", text: "Beim Umzug helfen, die Kiste fällt – juristisch nicht haftbar, moralisch schon. Gute Tarife zahlen trotzdem." },
          { title: "Mietsachschäden", text: "Kratzer in der Einbauküche oder ein Sprung in der Badfliese? Die Versicherung erstattet Schäden an der Mietwohnung." },
          { title: "Schäden durch deliktunfähige Kinder", text: "Kinder unter sieben Jahren sind nicht haftbar. Top-Tarife zahlen trotzdem und wahren so den Familien- und Nachbarschaftsfrieden." },
          { title: "Schäden im Ehrenamt", text: "Verursachst Du im Ehrenamt einen Schaden, springt die Privathaftpflicht ein, sofern der Verein selbst keine Vorsorge getroffen hat." },
          { title: "Drohnen & Hobbys", text: "Auch der Betrieb privater Drohnen unterhalb gewisser Gewichts- und Antriebsgrenzen ist über gute Tarife abgedeckt." },
        ],
      },
      {
        heading: "Was ist abgesichert?",
        bullets: [
          { title: "Personenschäden", text: "Medizinische Behandlung, Schmerzensgeld und Verdienstausfall, wenn Du jemanden verletzt." },
          { title: "Sachschäden", text: "Reparatur oder Ersatz von beschädigtem Eigentum Dritter – vom Smartphone bis zum Fahrzeug." },
          { title: "Vermögensschäden", text: "Finanzielle Folgeschäden ohne direkten Sach- oder Personenschaden, etwa wenn jemand durch Dein Verschulden Einkünfte verliert." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzlich verursachte Schäden",
      "Eigenschäden oder Schäden an mitversicherten Personen",
      "Schäden an gemieteten oder geliehenen Gegenständen (außer Mietwohnung)",
      "Berufliche Tätigkeiten – dafür gibt es die Berufshaftpflicht",
    ],
    conclusion:
      "Wenige Euro im Monat schützen Dich vor Forderungen, die Deine Existenz bedrohen können. Wir vergleichen die Top-Tarife und finden den passenden Schutz für Deine Lebenssituation.",
  },
  {
    slug: "hausratversicherung",
    metaTitle: "Hausratversicherung – Einrichtung absichern | FINABLE",
    metaDescription: "Hausratversicherung für Einbruch, Feuer und Leitungswasser: Deine Einrichtung zum Neuwert absichern und passende Deckungssumme richtig festlegen.",
    name: "Hausrat­versicherung",
    shortName: "Hausrat",
    category: "Sachversicherungen",
    icon: Sofa,
    tagline: "Dein Zuhause und alles darin – abgesichert.",
    intro:
      "Brand, Einbruch, Leitungswasser oder Sturm: Wenn Dein Hab und Gut beschädigt wird, geht es schnell um fünfstellige Beträge. Die Hausratversicherung ersetzt Deine Möbel, Elektronik, Kleidung und alle beweglichen Gegenstände im Haushalt zum Neuwert.",
    forWhom:
      "Für alle, die in einer eigenen Wohnung leben – ob Mieter:in oder Eigentümer:in. Besonders wichtig, wenn Du Wert auf hochwertige Einrichtung, Technik oder Fahrräder legst.",
    highlights: ["Neuwertentschädigung", "Schutz bei Einbruchdiebstahl", "Fahrraddiebstahl optional", "Elementarschäden mitversicherbar"],
    sections: [
      {
        heading: "Versicherte Gefahren",
        bullets: [
          { title: "Feuer", text: "Brand, Blitzschlag, Explosion und Implosion – auch Sengschäden in Top-Tarifen." },
          { title: "Einbruchdiebstahl & Vandalismus", text: "Inklusive Raub und Plünderung sowie Schäden durch das Aufbrechen der Wohnungstür." },
          { title: "Leitungswasser", text: "Rohrbruch, defekte Wasserzuleitungen und auslaufendes Wasser aus Aquarien oder Waschmaschinen." },
          { title: "Sturm & Hagel", text: "Ab Windstärke 8 sind alle Schäden am Hausrat abgedeckt – inkl. Folgeschäden durch eindringendes Wasser." },
        ],
      },
      {
        heading: "Sinnvolle Erweiterungen",
        bullets: [
          { title: "Fahrraddiebstahl", text: "Schutz auch außerhalb der eigenen vier Wände – meist in Höhe von 1–10 % der Versicherungssumme." },
          { title: "Elementarschäden", text: "Hochwasser, Starkregen, Rückstau, Erdrutsch, Schneedruck – angesichts des Klimawandels fast überall sinnvoll." },
          { title: "Glasbruch", text: "Mobiliar- und Gebäudeverglasung inklusive Cerankochfeld und Glaskeramik." },
          { title: "Grobe Fahrlässigkeit", text: "Top-Tarife verzichten auf Kürzungen, wenn z. B. die Tür mal offen bleibt oder die Kerze brennt." },
          { title: "Außenversicherung", text: "Hausrat, der vorübergehend nicht zu Hause ist (Hotel, Studienwohnung, Reisegepäck), bleibt geschützt." },
        ],
      },
      {
        heading: "Richtige Versicherungssumme bestimmen",
        paragraphs: [
          "Faustregel: 650 € pro m² Wohnfläche ergeben in den meisten Haushalten eine Unterversicherungs­verzichtsgrenze. Bei hochwertiger Einrichtung sollte die Summe individuell berechnet werden – sonst kürzt der Versicherer im Schadenfall anteilig.",
        ],
      },
    ],
    notCovered: [
      "Grob fahrlässig oder vorsätzlich herbeigeführte Schäden (je nach Tarif)",
      "Schäden am Gebäude selbst – dafür ist die Wohngebäudeversicherung zuständig",
      "Wertgegenstände über die vereinbarte Entschädigungsgrenze hinaus",
      "Schäden durch unterlassene Instandhaltung oder Verschleiß",
    ],
    conclusion:
      "Die Versicherungssumme muss zur tatsächlichen Wertdichte Deines Hausrats passen. Wir berechnen die richtige Summe und vergleichen Top-Tarife, damit Du im Schadenfall wirklich abgesichert bist.",
  },
  {
    slug: "wohngebaeudeversicherung",
    metaTitle: "Wohngebäudeversicherung vergleichen | FINABLE",
    metaDescription: "Wohngebäudeversicherung mit Elementarschutz: Dein Haus gegen Sturm, Feuer und Leitungswasser absichern und Tarife bedarfsgerecht vergleichen lassen.",
    name: "Wohngebäude­versicherung",
    shortName: "Wohngebäude",
    category: "Sachversicherungen",
    icon: Home,
    tagline: "Schutz für die größte Investition Deines Lebens.",
    intro:
      "Ein Hausbrand oder ein Wasserschaden kann Dein Eigenheim in eine finanzielle Ruine verwandeln. Die Wohngebäudeversicherung schützt die Bausubstanz inklusive fest verbauter Bestandteile und ist für Eigentümer:innen unverzichtbar – bei finanzierten Immobilien meist sogar Pflicht.",
    forWhom: "Für alle Eigentümer:innen von Häusern, Eigentumswohnungen und vermieteten Objekten.",
    highlights: [
      "Versicherung zum gleitenden Neuwert",
      "Feuer, Leitungswasser, Sturm/Hagel als Grundschutz",
      "Elementarschäden zubuchbar",
      "Mietausfall bei Unbewohnbarkeit",
    ],
    sections: [
      {
        heading: "Versicherte Bausubstanz",
        paragraphs: [
          "Mauerwerk, Dach, fest verbaute Sanitär- und Heizungsanlagen, eingebaute Schränke, Photovoltaikanlagen sowie Garagen und Carports auf demselben Grundstück. Auch Nebengebäude wie Gartenhäuser können in den Schutz aufgenommen werden.",
        ],
      },
      {
        heading: "Versicherte Gefahren im Grundschutz",
        bullets: [
          { title: "Feuer", text: "Brand, Blitzschlag, Explosion, Implosion sowie Anprall von Luftfahrzeugen." },
          { title: "Leitungswasser", text: "Schäden durch Rohrbruch, Frost und auslaufendes Wasser – inklusive Folgeschäden." },
          { title: "Sturm & Hagel", text: "Ab Windstärke 8 abgedeckt – inkl. abgedeckter Dachziegel und beschädigter Fassaden." },
          { title: "Mietausfall", text: "Wird die Immobilie unbewohnbar, übernimmt die Versicherung bis zu 24 Monate Mietausfall." },
        ],
      },
      {
        heading: "Elementarschäden – immer wichtiger",
        bullets: [
          { title: "Hochwasser & Überschwemmung", text: "Steigende Pegel werden auch fernab von Flüssen zum Risiko." },
          { title: "Starkregen & Rückstau", text: "Wasser, das ins Haus drückt, ist im Standard nicht enthalten – Top-3-Schadenursache der letzten Jahre." },
          { title: "Erdbeben, Erdrutsch, Schneedruck", text: "Naturgefahren, die regional sehr unterschiedlich relevant sind – wir prüfen die Gefahrenklasse Deines Standorts." },
        ],
      },
      {
        heading: "Sinnvolle Bausteine",
        bullets: [
          { title: "Glasbruch", text: "Verglasungen an Fenstern, Türen und Wintergärten." },
          { title: "Photovoltaik-Erweiterung", text: "Spezielle Deckung für Schäden an Modulen, Wechselrichtern und Mindererträgen." },
          { title: "Ableitungsrohre außerhalb des Gebäudes", text: "Wichtig bei älteren Häusern mit langen Zuleitungen auf dem Grundstück." },
        ],
      },
    ],
    notCovered: [
      "Hausrat – dafür gibt es die Hausratversicherung",
      "Glasbruch ohne entsprechenden Zusatzbaustein",
      "Schäden durch unterlassene Instandhaltung",
      "Schäden, die schon vor Vertragsbeginn bestanden",
    ],
    conclusion:
      "Wir prüfen für Dich Versicherungssumme, Bedingungen und sinnvolle Bausteine wie Elementar oder Glas – und sorgen dafür, dass Dein Eigentum lückenlos geschützt ist.",
  },
  {
    slug: "kfz-versicherung",
    metaTitle: "Kfz-Versicherung vergleichen und wechseln | FINABLE",
    metaDescription: "Kfz-Versicherung passend zu Deinem Fahrprofil: Haftpflicht, Teil- und Vollkasko vergleichen, Beitrag senken und Leistungen fair im Blick behalten.",
    name: "KFZ-Versicherung",
    shortName: "KFZ",
    category: "Sachversicherungen",
    icon: Car,
    tagline: "Pflichtschutz mit Sparpotenzial.",
    intro:
      "Ohne KFZ-Haftpflicht darf in Deutschland kein Fahrzeug bewegt werden. Doch die Beiträge unterscheiden sich je nach Anbieter teils um mehrere Hundert Euro pro Jahr. Wir vergleichen jährlich für Dich und finden den besten Tarif für Dein Profil.",
    forWhom: "Für jeden Fahrzeughalter – vom Erstwagen bis zum Familienfuhrpark.",
    highlights: [
      "Haftpflicht, Teil- oder Vollkasko",
      "Mit Werkstattbindung Beiträge sparen",
      "Rabattschutz nach Schadenfällen",
      "Telematik-Tarife für Fahranfänger",
    ],
    sections: [
      {
        heading: "Die drei Bausteine",
        bullets: [
          { title: "Haftpflicht (Pflicht)", text: "Übernimmt Schäden, die Du anderen zufügst – Personen, Sachen, Vermögen. Mindestdeckung 7,5 Mio. €, sinnvoll sind 100 Mio. € pauschal." },
          { title: "Teilkasko", text: "Schützt Dein Auto vor Diebstahl, Glasbruch, Wildunfall, Sturm, Hagel, Brand und Marderbiss inkl. Folgeschäden." },
          { title: "Vollkasko", text: "Zusätzlich zur Teilkasko: selbst verschuldete Unfälle, Vandalismus und Schäden durch unbekannte Dritte." },
        ],
      },
      {
        heading: "So sparst Du beim Beitrag",
        bullets: [
          { title: "Werkstattbindung", text: "Reparatur in einer Partnerwerkstatt – oft 20 % Beitragsersparnis." },
          { title: "Jährliche Zahlung", text: "Spart gegenüber monatlicher Zahlweise bis zu 5 %." },
          { title: "SF-Klasse übertragen", text: "Schadenfreiheitsklassen lassen sich innerhalb der Familie weitergeben – v. a. an Kinder mit eigenem Auto." },
          { title: "Jahreskilometer realistisch angeben", text: "Weniger gefahrene Kilometer = günstigerer Beitrag." },
          { title: "Telematik", text: "Fahranfänger können durch defensives Fahren bis zu 30 % sparen." },
          { title: "Wechseltermin nutzen", text: "Stichtag 30.11. – wir prüfen jährlich automatisch und sparen oft dreistellige Beträge." },
        ],
      },
      {
        heading: "Sinnvolle Zusatzbausteine",
        bullets: [
          { title: "Rabattschutz", text: "Ein Schaden pro Jahr ohne Rückstufung – wichtig bei viel Pendelverkehr." },
          { title: "Neuwertentschädigung", text: "Bei Totalschaden in den ersten 24 Monaten erstattet der Versicherer den Neupreis." },
          { title: "Auslandsschadenschutz", text: "Bei Unfällen im Ausland regulierst Du nach deutschem Recht – meist zu Deinem Vorteil." },
          { title: "Schutzbrief", text: "Pannen- und Abschlepphilfe rund um die Uhr – oft günstiger als der ADAC." },
        ],
      },
    ],
    notCovered: ["Vorsätzlich verursachte Schäden", "Schäden unter Alkohol- oder Drogeneinfluss", "Verschleiß und Wartung", "Schäden bei nicht zugelassenen Rennen"],
    conclusion:
      "Die KFZ-Versicherung ist einer der einfachsten Hebel zum Sparen. Wir vergleichen jährlich vor dem Stichtag (30.11.) und wechseln auf Wunsch automatisch zum besten Tarif.",
  },
  {
    slug: "unfallversicherung",
    metaTitle: "Private Unfallversicherung vergleichen | FINABLE",
    metaDescription: "Private Unfallversicherung: dauerhafte Unfallfolgen finanziell absichern, Invaliditätsleistung sinnvoll bemessen und leistungsstarke Tarife vergleichen.",
    name: "Private Unfall­versicherung",
    shortName: "Unfall",
    category: "Sachversicherungen",
    icon: HeartPulse,
    tagline: "Absicherung rund um die Uhr – privat wie beruflich.",
    intro:
      "Die gesetzliche Unfallversicherung greift nur bei Unfällen am Arbeitsplatz oder auf dem Arbeitsweg. Die private Unfallversicherung ergänzt diesen Schutz für Freizeit und Alltag und zahlt eine einmalige Invaliditätssumme, wenn Du dauerhaft beeinträchtigt bist.",
    forWhom: "Für Kinder, Selbstständige, Hausfrauen und -männer, Rentner:innen sowie alle aktiven Menschen mit Hobbys oder erhöhtem Freizeitrisiko.",
    highlights: ["Weltweiter 24/7-Schutz", "Einmalige Kapitalleistung bei Invalidität", "Optional: Unfallrente, Krankenhaustagegeld", "Sofortleistung bei schweren Verletzungen"],
    sections: [
      {
        heading: "Wichtige Bausteine",
        bullets: [
          { title: "Progression", text: "Bei schweren Invaliditätsgraden vervielfacht sich die Versicherungssumme – meist 225 % bis 500 %. Damit erreichst Du auch bei kleinerer Grundsumme hohe Auszahlungen." },
          { title: "Unfallrente", text: "Lebenslange monatliche Rente ab einem bestimmten Invaliditätsgrad – wichtige Ergänzung für Familien." },
          { title: "Kosmetische Operationen", text: "Übernahme der Kosten bei unfallbedingten Eingriffen – bis zu mehreren zehntausend Euro." },
          { title: "Bergungskosten", text: "Wichtig für alle, die Wintersport oder Outdoor-Aktivitäten betreiben." },
          { title: "Krankenhaustagegeld", text: "Tägliche Auszahlung während eines unfallbedingten Klinikaufenthalts." },
          { title: "Sofortleistung", text: "Bei schweren Verletzungen wie Querschnittslähmung oder Verlust eines Sinnesorgans wird ein Vorschuss gezahlt." },
        ],
      },
      {
        heading: "Wann zahlt die Unfallversicherung?",
        paragraphs: [
          "Voraussetzung ist eine bleibende Beeinträchtigung (Invalidität) infolge eines Unfalls innerhalb eines Jahres, festgestellt innerhalb von 15 Monaten. Die Höhe der Auszahlung richtet sich nach der sogenannten Gliedertaxe – je nach betroffenem Körperteil.",
        ],
      },
      {
        heading: "Erweiterter Unfallbegriff",
        bullets: [
          { title: "Eigenbewegungen", text: "Auch Verrenkungen oder Zerrungen ohne äußere Einwirkung gelten als Unfall." },
          { title: "Erhöhte Kraftanstrengung", text: "Bandscheibenvorfälle durch Heben sind in Top-Tarifen abgedeckt." },
          { title: "Bewusstseinsstörungen durch Krankheit", text: "Stürze infolge Herzinfarkt oder Schlaganfall werden anerkannt." },
        ],
      },
    ],
    notCovered: ["Krankheiten und Krankheitsfolgen", "Bewusstseinsstörungen durch Alkohol oder Drogen", "Vorsätzliche Selbstverletzungen", "Unfälle bei nicht zugelassenen Rennen"],
    conclusion:
      "Wir analysieren Dein persönliches Risikoprofil und stellen eine Unfallversicherung mit der passenden Invaliditätssumme, Progression und sinnvollen Zusatzbausteinen zusammen.",
  },
  {
    slug: "tierhalterhaftpflicht",
    metaTitle: "Tierhalterhaftpflicht für Hund & Pferd | FINABLE",
    metaDescription: "Tierhalterhaftpflicht für Hund und Pferd: Schäden durch Dein Tier zuverlässig abdecken und leistungsstarke Tarife mit hoher Deckungssumme vergleichen.",
    name: "Tierhalter­haftpflicht­versicherung",
    shortName: "Tierhalterhaftpflicht",
    category: "Sachversicherungen",
    icon: Dog,
    tagline: "Für alle, die Hund, Pferd & Co. ein Zuhause geben.",
    intro:
      "Wenn Dein Hund einen Radfahrer zu Fall bringt oder Dein Pferd ein Auto beschädigt, haftest Du als Halter:in unbegrenzt – auch ohne eigenes Verschulden. Die Tierhalterhaftpflicht übernimmt diese Kosten und ist in einigen Bundesländern für Hunde sogar Pflicht.",
    forWhom: "Für alle Halter:innen von Hunden, Pferden und exotischen Tieren. Für Katzen und Kleintiere reicht die normale Privathaftpflicht.",
    highlights: ["Personen-, Sach- und Vermögensschäden", "Mietsachschäden in Ferienwohnungen", "Welpen oft beitragsfrei mitversichert", "Schutz auch im Ausland"],
    sections: [
      {
        heading: "Wichtige Leistungen guter Tarife",
        bullets: [
          { title: "Forderungsausfalldeckung", text: "Greift, wenn Dir selbst durch ein fremdes Tier ein Schaden entsteht und der Halter nicht zahlen kann." },
          { title: "Hüter- und Hundetrainerschäden", text: "Auch wenn jemand anderes auf Dein Tier aufpasst, bleibt der Schutz bestehen." },
          { title: "Deckschäden", text: "Wichtig bei Zuchttieren – inklusive Folgeschäden wie ungewollter Trächtigkeit." },
          { title: "Flurschäden", text: "Wenn Dein Pferd auf fremder Weide Schäden verursacht." },
          { title: "Mietsachschäden", text: "Kratzer im Parkett der Ferienwohnung oder Schäden in der Hundepension." },
          { title: "Auslandsschutz", text: "Mindestens 12 Monate weltweit – wichtig für längere Reisen oder Umzüge." },
        ],
      },
      {
        heading: "Was ist abgesichert?",
        paragraphs: [
          "Als Tierhalter:in haftest Du verschuldensunabhängig (§ 833 BGB) – auch wenn Dein Tier sich völlig unerwartet verhält. Beißt der Hund, kommt es zum Pferd-PKW-Zusammenstoß oder reißt sich die Katze los und verursacht einen Auffahrunfall, geht es schnell um sechsstellige Summen. Die Versicherung übernimmt Personen-, Sach- und Vermögensschäden bis zur vereinbarten Deckungssumme (mind. 10 Mio. €).",
        ],
      },
      {
        heading: "Tipps für die Tarifwahl",
        bullets: [
          { title: "Hohe Deckungssumme", text: "Mindestens 10 Mio. €, besser 20–50 Mio. € – Personenschäden können extrem teuer werden." },
          { title: "Niedrige Selbstbeteiligung", text: "Häufige Schäden (Leinen-Riss, kleine Bisse) lohnen sich nur bei niedrigem Eigenanteil." },
          { title: "Mehrere Tiere", text: "Top-Tarife versichern Welpen oder Fohlen für 6–12 Monate beitragsfrei mit." },
          { title: "Listenhunde / bestimmte Rassen", text: "Spezialtarife sind oft günstiger als Standardanbieter, die Aufschläge nehmen." },
        ],
      },
    ],
    notCovered: ["Schäden, die Du selbst durch Dein Tier erleidest", "Vorsätzlich herbeigeführte Schäden", "Gewerbliche Tierhaltung ohne entsprechenden Tarif", "Schäden bei Hundekämpfen oder vergleichbaren verbotenen Veranstaltungen"],
    conclusion:
      "Wir vergleichen Tarife mit hoher Versicherungssumme (mind. 10 Mio. €), wichtigen Zusatzbausteinen und fairer Selbstbeteiligung – passend zu Deinem Tier und Deiner Lebenssituation.",
  },
  make({
    slug: "rechtsschutzversicherung",
    metaTitle: "Rechtsschutzversicherung vergleichen | FINABLE",
    metaDescription: "Rechtsschutzversicherung für Privat, Beruf und Verkehr: Anwalts- und Gerichtskosten absichern, Bausteine gezielt wählen und Tarife fair vergleichen.",
    name: "Rechtsschutz­versicherung",
    shortName: "Rechtsschutz",
    category: "Sachversicherungen",
    icon: Scale,
    tagline: "Sein Recht durchsetzen – ohne Angst vor den Kosten.",
    intro:
      "Ein Streit mit Vermieter:in, Arbeitgeber:in oder nach einem Verkehrsunfall kann schnell mehrere tausend Euro kosten. Die Rechtsschutzversicherung übernimmt Anwalts-, Gerichts- und Gutachterkosten – inklusive einer kostenlosen telefonischen Erstberatung.",
    forWhom: "Für alle, die ihr Recht selbstbewusst durchsetzen wollen – besonders sinnvoll für Berufstätige, Mieter:innen und Autofahrer:innen.",
    highlights: ["Privat-, Berufs-, Verkehrs- und Mietrechtsschutz", "Anwaltliche Erstberatung inklusive", "Mediation als Konfliktlösung", "Weltweiter Schutz möglich"],
    sections: [
      {
        heading: "Bausteine im Überblick",
        bullets: [
          { title: "Privatrechtsschutz", text: "Streitigkeiten als Verbraucher:in – Online-Käufe, Reklamationen, Schadenersatz, Erbrecht." },
          { title: "Berufsrechtsschutz", text: "Kündigung, Abfindung, Arbeitszeugnis, Mobbing – mit Abstand häufigster Anwendungsfall." },
          { title: "Verkehrsrechtsschutz", text: "Unfall, Bußgeld, Führerscheinentzug, Strafrechtsschutz nach Verkehrsdelikten – auch ohne weitere Bausteine abschließbar." },
          { title: "Mietrechtsschutz", text: "Nebenkostenabrechnung, Eigenbedarfskündigung, Mängel – Schutz für Mieter:innen und Eigentümer:innen." },
          { title: "Steuerrechtsschutz", text: "Vor Finanzgerichten – wichtig bei strittigen Bescheiden und Selbstständigkeit." },
          { title: "Sozialrechtsschutz", text: "Streit um Renten, Krankengeld, Pflegegrade oder Bürgergeld vor den Sozialgerichten." },
        ],
      },
      {
        heading: "Worauf Du beim Tarif achten solltest",
        bullets: [
          { title: "Kurze Wartezeit", text: "Im Privat- und Verkehrsrecht greift der Schutz oft sofort, im Arbeits- und Mietrecht meist nach 3 Monaten." },
          { title: "Hohe Deckungssumme", text: "Mindestens 1 Mio. € pro Fall, besser unbegrenzt – Gerichtsverfahren über mehrere Instanzen werden teuer." },
          { title: "Freie Anwaltswahl", text: "Du suchst Dir den Anwalt Deines Vertrauens aus – nicht der Versicherer." },
          { title: "Mediation inklusive", text: "Außergerichtliche Konfliktlösung spart Zeit, Nerven und schont Beziehungen." },
          { title: "Niedrige Selbstbeteiligung", text: "150 € sind ein guter Kompromiss zwischen Beitrag und Eigenanteil im Schadenfall." },
        ],
      },
      {
        heading: "Was übernommen wird",
        paragraphs: [
          "Anwaltskosten, Gerichtskosten, Sachverständigen­honorare, Zeugengelder, Übersetzungskosten und ggf. die gegnerischen Kosten bei verlorenem Prozess – im Rahmen der vereinbarten Deckungssumme.",
        ],
      },
    ],
    notCovered: ["Bereits vor Vertragsabschluss bekannte Streitigkeiten", "Familienrechtliche Auseinandersetzungen (außer Erstberatung)", "Vorsätzliche Straftaten", "Streitigkeiten zwischen mitversicherten Personen", "Bauherrenrisiken (eigenes Modul)"],
    conclusion:
      "Achte auf kurze Wartezeiten und eine hohe Deckungssumme. Wir vergleichen die wichtigsten Bedingungen und finden den Tarif, der zu Deiner Lebenssituation passt.",
  }),

  // ─────────────── Gesundheitsvorsorge ───────────────
  make({
    slug: "private-krankenvollversicherung",
    metaTitle: "Private Krankenversicherung (PKV) beraten | FINABLE",
    metaDescription: "Private Krankenversicherung mit Weitblick: Tarife, Beitrag im Alter und Wechseloptionen prüfen und unabhängig zur passenden PKV-Lösung beraten lassen.",
    name: "Private Kranken­vollversicherung",
    shortName: "Private Krankenversicherung",
    category: "Gesundheitsvorsorge",
    icon: Stethoscope,
    tagline: "Beste medizinische Versorgung – ein Leben lang.",
    intro:
      "Die PKV bietet Leistungen, die in der gesetzlichen Krankenversicherung oft nicht enthalten sind: freie Arztwahl, kürzere Wartezeiten, Chefarztbehandlung, Einbettzimmer und moderne Behandlungsmethoden. Sinnvoll für Beamte, Selbstständige und Angestellte über der Versicherungspflichtgrenze.",
    forWhom: "Für Beamte (mit Beihilfe), Selbstständige sowie Angestellte mit einem Bruttoeinkommen über der JAEG.",
    highlights: ["Leistungen lebenslang garantiert", "Beitragsentlastung im Alter planbar", "Beihilfekonforme Tarife für Beamte", "Optionale Bausteine wie Krankentagegeld"],
    sections: [
      {
        heading: "Worauf Du achten solltest",
        bullets: [
          { title: "Bedingungsqualität", text: "Welche Leistungen sind vertraglich garantiert – und nicht nur Kulanz? Heilmittel, Hilfsmittel, Psychotherapie, neue Behandlungsmethoden müssen klar definiert sein." },
          { title: "Beitragsstabilität", text: "Wir bewerten Anbieter nach historischer Beitragsentwicklung, nicht nach Lockangeboten oder Rechenbeispielen." },
          { title: "Altersrückstellungen", text: "Sorgen dafür, dass die Beiträge im Alter bezahlbar bleiben – ein Wechsel des Anbieters kostet diese in der Regel." },
          { title: "Beihilfeberechtigung", text: "Beamte zahlen oft nur für 30–50 % – die PKV ergänzt den Rest. Tarif muss exakt zur Beihilfeverordnung passen." },
        ],
      },
      {
        heading: "Typische Leistungsbausteine",
        bullets: [
          { title: "Ambulant", text: "Freie Arzt- und Heilpraktikerwahl, Erstattung über GOÄ-Höchstsätze, Vorsorge ohne Wartezeit." },
          { title: "Stationär", text: "Ein- oder Zweibettzimmer, Chefarztbehandlung, freie Klinikwahl auch bundesweit." },
          { title: "Zahn", text: "80–100 % für Zahnersatz, Implantate, Inlays sowie professionelle Zahnreinigung." },
          { title: "Krankentagegeld", text: "Lückenloser Einkommensschutz – ab Karenztag selbst wählbar." },
          { title: "Pflegezusatz", text: "Sinnvoll bei höherem Einkommen, um Versorgungslücken im Pflegefall zu schließen." },
        ],
      },
      {
        heading: "Vor dem Wechsel kritisch prüfen",
        paragraphs: [
          "Der Wechsel in die PKV ist eine Entscheidung fürs Leben – die Rückkehr in die GKV ist nur unter sehr engen Voraussetzungen möglich. Wir analysieren langfristig: Familienplanung, Karrierepfad, Beihilfe und Renteneintritt.",
          "Vor jedem Antrag prüfen wir Vorerkrankungen anonym über eine Risikovoranfrage – so vermeidest Du Ausschlüsse oder Risikozuschläge.",
        ],
      },
    ],
    notCovered: [
      "Vor Antrag bekannte Erkrankungen ohne korrekte Anzeige",
      "Behandlungen rein kosmetischer Natur",
      "Leistungen außerhalb des vereinbarten Tarifumfangs",
      "Aufwendungen über GOÄ-Höchstsätze hinaus (in Standardtarifen)",
    ],
    conclusion:
      "Die PKV ist eine Entscheidung fürs Leben. Wir analysieren Deine Situation langfristig, prüfen Bedingungen anonym vor dem Antrag und finden den Tarif mit der besten Balance aus Leistung und Beitragsstabilität.",
  }),
  make({
    slug: "zahnzusatzversicherung",
    metaTitle: "Zahnzusatzversicherung vergleichen | FINABLE",
    metaDescription: "Zahnzusatzversicherung mit hohen Leistungen für Zahnersatz, Prophylaxe und Kieferorthopädie: Tarife unabhängig vergleichen und passend zu Dir wählen.",
    name: "Zahnzusatz­versicherung",
    shortName: "Zahnzusatz",
    category: "Gesundheitsvorsorge",
    icon: Smile,
    tagline: "Implantate, Inlays und Zahnersatz – ohne hohe Eigenkosten.",
    intro:
      "Die gesetzliche Krankenversicherung zahlt nur einen kleinen Festzuschuss für Zahnersatz. Ein Implantat kostet schnell 2.000–4.000 €. Die Zahnzusatzversicherung übernimmt bis zu 100 % der verbleibenden Kosten und sorgt für hochwertigen Zahnersatz ohne finanzielle Sorgen.",
    forWhom: "Für alle gesetzlich Versicherten – am besten so früh wie möglich abschließen, da Wartezeiten und Vorerkrankungen den Schutz einschränken.",
    highlights: ["Bis zu 100 % Erstattung für Zahnersatz", "Professionelle Zahnreinigung inklusive", "Kieferorthopädie für Kinder", "Keine Wartezeit bei Top-Tarifen"],
    sections: [
      {
        heading: "Was Top-Tarife leisten",
        bullets: [
          { title: "Zahnersatz bis 100 %", text: "Implantate, Brücken, Kronen und Inlays werden inklusive Eigenanteil erstattet – ohne dass Du tausende Euro vorstrecken musst." },
          { title: "Zahnerhalt & Wurzelbehandlung", text: "Hochwertige Kunststofffüllungen, Wurzelkanalbehandlungen und Parodontose-Therapie sind im Top-Tarif enthalten." },
          { title: "Professionelle Zahnreinigung", text: "Ein- bis zweimal pro Jahr ohne Eigenanteil – die wichtigste Vorsorge gegen Karies und Parodontose." },
          { title: "Kieferorthopädie für Kinder", text: "Auch wenn die GKV nur ab KIG 3 zahlt: Top-Tarife übernehmen Zahnspangen schon bei niedrigeren Einstufungen." },
        ],
      },
      {
        heading: "Wann lohnt sich der Abschluss?",
        paragraphs: [
          "Je früher, desto besser: Top-Tarife haben Wartezeiten und sogenannte Zahnstaffeln, die in den ersten Jahren die Leistungen begrenzen. Wer schon eine angeratene Behandlung hat, bekommt für diese keinen Schutz mehr.",
          "Auch für Kinder ist ein früher Abschluss sinnvoll – idealerweise vor dem Wechsel zum bleibenden Gebiss, um spätere Kieferorthopädie abzusichern.",
        ],
      },
    ],
    notCovered: [
      "Bereits angeratene oder begonnene Behandlungen",
      "Rein kosmetische Eingriffe ohne medizinischen Grund",
      "Behandlungen während der Wartezeit",
      "Leistungen über die Zahnstaffel hinaus in den ersten Versicherungsjahren",
    ],
    conclusion:
      "Wir prüfen Erstattungssätze, Summenbegrenzungen und Wartezeiten – und finden den Tarif, der bei Deinem Zahnstatus die beste Leistung bringt.",
  }),
  make({
    slug: "krankentagegeld",
    metaTitle: "Krankentagegeldversicherung schließt Lücke | FINABLE",
    metaDescription: "Krankentagegeldversicherung schließt die Lücke nach dem Krankengeld: Höhe passgenau berechnen und leistungsstarke Tarife unabhängig vergleichen.",
    name: "Krankentagegeld­versicherung",
    shortName: "Krankentagegeld",
    category: "Gesundheitsvorsorge",
    icon: Accessibility,
    tagline: "Einkommensschutz bei längerer Krankheit.",
    intro:
      "Wer länger als sechs Wochen krank ist, erhält von der Krankenkasse nur noch Krankengeld – das deutlich unter dem Nettoeinkommen liegt. Selbstständige stehen sogar von Tag eins ohne Lohnfortzahlung da. Die Krankentagegeld­versicherung schließt diese Einkommenslücke.",
    forWhom: "Selbstständige, Freiberufler:innen sowie Angestellte mit hohem Einkommen, die ihre Versorgungslücke schließen wollen.",
    highlights: ["Tagegeld ab dem gewünschten Karenztag", "Lückenlose Einkommensabsicherung", "Kombinierbar mit der PKV", "Steuerlich begünstigt"],
    sections: [
      {
        heading: "Wie groß ist Deine Versorgungslücke?",
        paragraphs: [
          "Angestellte erhalten ab dem 43. Krankheitstag nur noch das gesetzliche Krankengeld – maximal 90 % des Nettoeinkommens, gedeckelt durch die Beitragsbemessungsgrenze. Bei höherem Einkommen klafft schnell eine Lücke von mehreren hundert Euro pro Monat.",
          "Selbstständige und Freiberufler:innen stehen ohne entsprechende Tarife sogar ab dem ersten Krankheitstag ohne Einkommen da.",
        ],
      },
      {
        heading: "Worauf Du beim Tarif achten solltest",
        bullets: [
          { title: "Karenzzeit passend wählen", text: "Angestellte ab Tag 43, Selbstständige je nach Rücklagen ab Tag 15, 22 oder 43 – je länger die Karenz, desto günstiger der Beitrag." },
          { title: "Dynamik einplanen", text: "Tagegeld sollte mit Deinem Einkommen mitwachsen, damit auch nach Jahren die Lücke geschlossen bleibt." },
          { title: "Keine Überversicherung", text: "Das Tagegeld darf nicht höher sein als Dein Netto – sonst kürzt der Versicherer im Leistungsfall." },
          { title: "Nahtloser Übergang zur Berufsunfähigkeitsversicherung", text: "Geht die Krankheit in eine Berufsunfähigkeit über, sollte der Tarif ohne Lücke in die Berufsunfähigkeitsleistung übergehen." },
        ],
      },
    ],
    notCovered: [
      "Krankheiten, die bei Antragstellung verschwiegen wurden",
      "Tagegeld über dem nachgewiesenen Nettoeinkommen",
      "Vorsätzlich herbeigeführte Erkrankungen",
    ],
    conclusion:
      "Wir berechnen Deine individuelle Versorgungslücke und finden den Tarif mit der passenden Karenzzeit und einem fairen Beitrag.",
  }),

  // ─────────────── Altersvorsorge & Vermögensaufbau ───────────────
  make({
    slug: "private-rentenversicherung",
    metaTitle: "Private Rentenversicherung – Altersvorsorge | FINABLE",
    metaDescription: "Private Rentenversicherung als flexibler Baustein der Altersvorsorge: fondsgebundene Tarife vergleichen und lebenslange Rente steuerbegünstigt sichern.",
    name: "Private Renten­versicherung",
    shortName: "Private Rente",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: PiggyBank,
    tagline: "Steueroptimiert für die Rente sparen.",
    intro:
      "Die gesetzliche Rente wird für die meisten nicht zum Leben reichen. Die private Rentenversicherung baut steuerlich begünstigt ein zusätzliches Polster auf – flexibel als ETF-basierte Fondspolice oder als klassische Variante mit garantierter Verzinsung.",
    forWhom: "Für alle, die langfristig fürs Alter vorsorgen und dabei Steuervorteile nutzen möchten.",
    highlights: ["Halbeinkünfteverfahren in der Auszahlphase", "ETF-Sparen ohne Abgeltungsteuer", "Lebenslange Rente oder Kapitalauszahlung", "Flexibel anpassbar"],
    sections: [
      {
        heading: "Steuerliche Vorteile",
        bullets: [
          { title: "Keine Abgeltungsteuer in der Ansparphase", text: "Erträge wachsen steuerfrei – ein massiver Renditevorteil gegenüber dem normalen Depot über Jahrzehnte." },
          { title: "Halbeinkünfteverfahren bei Kapitalauszahlung", text: "Bei Auszahlung nach dem 62. Geburtstag und mind. 12 Jahren Laufzeit zahlst Du nur auf die halben Erträge Steuern." },
          { title: "Lebenslange Rente steuerlich begünstigt", text: "Bei Verrentung wird nur der Ertragsanteil besteuert – mit 67 Jahren z. B. nur 17 %." },
        ],
      },
      {
        heading: "Fonds- oder klassische Police?",
        bullets: [
          { title: "Fondspolice (ETF-basiert)", text: "Höhere Renditechancen durch Aktien-/ETF-Anlage. Ideal bei langem Anlagehorizont." },
          { title: "Klassische Variante", text: "Garantierter Rechnungszins plus Überschussbeteiligung – sicher, aber renditeschwach." },
          { title: "Hybrid-Modelle", text: "Kombinieren Garantie und Renditechance – sinnvoll für sicherheitsorientierte Sparer:innen." },
        ],
      },
      {
        heading: "Worauf wir bei der Auswahl achten",
        bullets: [
          { title: "Niedrige Kostenquote", text: "Effektivkosten unter 1 % p. a. sind unser Maßstab – jeder Prozentpunkt kostet über 30 Jahre Zehntausende." },
          { title: "Breite ETF-Auswahl", text: "MSCI World, EM, Themen-ETFs – kein Tunnel auf hauseigene Fonds." },
          { title: "Flexibilität", text: "Beitrag aussetzen, erhöhen, Zuzahlungen, Entnahmen – ohne Strafzuschläge." },
          { title: "Wahlrecht Rente / Kapital", text: "Du entscheidest erst mit 67, ob Du Kapital auf einen Schlag oder lebenslange Rente willst." },
        ],
      },
    ],
    notCovered: [
      "Frühe Auszahlung vor dem 62. Geburtstag ohne steuerliche Vorteile",
      "Garantierte Mindestrenditen (außer im klassischen Tarif)",
      "Inflationsausgleich, sofern keine Dynamik vereinbart",
    ],
    conclusion:
      "Wir vergleichen die Kostenquoten der Top-Anbieter, achten auf günstige ETF-Auswahl und gestalten den Vertrag so flexibel, dass er zu jeder Lebensphase passt.",
  }),
  make({
    slug: "ruerup-rente",
    metaTitle: "Rürup-Rente – Basisrente für Selbstständige | FINABLE",
    metaDescription: "Rürup-Rente (Basisrente) für Selbstständige und Gutverdiener: Beiträge bis 30.826 € steuerlich absetzen und lebenslange Rente flexibel aufbauen.",
    name: "Rürup-Rente (Basisrente)",
    shortName: "Rürup",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: Landmark,
    tagline: "Steuerlich hoch geförderte Vorsorge für Selbstständige & Gutverdiener.",
    intro:
      "Mit der Rürup-Rente kannst Du jährlich fünfstellige Beiträge steuerlich absetzen. Besonders attraktiv für Selbstständige ohne gesetzliche Rente und für Angestellte mit hohem Steuersatz. Die Auszahlung erfolgt als lebenslange Rente.",
    forWhom: "Selbstständige, Freiberufler:innen und Angestellte mit hohem Steuersatz.",
    highlights: ["Bis zu 100 % als Sonderausgaben absetzbar", "Pfändungs- und Hartz-IV-sicher in der Ansparphase", "Lebenslange Rente ab dem 62. Geburtstag", "Hinterbliebenenschutz möglich"],
    sections: [
      {
        heading: "Steuerlicher Hebel",
        paragraphs: [
          "Ab 2023 sind Beiträge zur Rürup-Rente zu 100 % als Sonderausgaben absetzbar – bis zum jährlichen Höchstbetrag (2026: 30.826 € für Ledige, 61.652 € für gemeinsam Veranlagte). Bei einem Spitzensteuersatz von 42 % bekommst Du also fast die Hälfte der Beiträge über die Steuererstattung zurück.",
        ],
      },
      {
        heading: "Für wen besonders attraktiv",
        bullets: [
          { title: "Selbstständige & Freiberufler:innen", text: "Ohne Pflicht zur gesetzlichen Rente – Rürup ist hier oft die effizienteste Vorsorge." },
          { title: "Gutverdienende Angestellte", text: "Spitzensteuersatz nutzen, um den Bruttohebel maximal mitzunehmen." },
          { title: "Personen mit Einmalzahlungen", text: "Abfindungen, Erbschaften oder Boni können steuerlich abgefedert werden." },
        ],
      },
      {
        heading: "Eigenschaften, die Du kennen solltest",
        bullets: [
          { title: "Keine Kapitalauszahlung", text: "Anders als bei der privaten Rente gibt es nur lebenslange Rente – kein Klumpensummen-Wahlrecht." },
          { title: "Vererbung eingeschränkt", text: "Nur Ehepartner und kindergeldberechtigte Kinder können über eine Hinterbliebenenrente partizipieren." },
          { title: "Pfändungssicher", text: "In der Ansparphase vor Gläubigerzugriff geschützt – auch bei Insolvenz." },
          { title: "Frühestens ab 62", text: "Auszahlung beginnt frühestens mit 62 Jahren als lebenslange Rente." },
        ],
      },
    ],
    notCovered: [
      "Kapitalwahlrecht (gibt es nicht)",
      "Frei vererbbare Auszahlung an beliebige Erben",
      "Garantierte Rendite über die Mindestverzinsung hinaus",
    ],
    conclusion:
      "Wir analysieren Deine Steuersituation und finden den Rürup-Vertrag mit niedrigen Kosten, breiter ETF-Auswahl und flexiblen Optionen.",
  }),
  make({
    slug: "betriebliche-altersvorsorge",
    metaTitle: "Betriebliche Altersvorsorge (bAV) beraten | FINABLE",
    metaDescription: "Betriebliche Altersvorsorge über den Arbeitgeber: Entgeltumwandlung, Zuschuss und Durchführungswege prüfen und passende bAV-Lösung gemeinsam finden.",
    name: "Betriebliche Altersvorsorge",
    shortName: "bAV",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: Users,
    tagline: "Mit Bruttogehalt sparen – Arbeitgeber zahlt mit.",
    intro:
      "Die bAV nutzt Steuer- und Sozialabgabenvorteile, um aus jedem investierten Euro mehr Rente zu machen. Seit 2019 müssen Arbeitgeber mindestens 15 % Zuschuss leisten – ein echter Hebel.",
    forWhom: "Für Angestellte mit Anspruch auf Entgeltumwandlung – also fast jede:n.",
    highlights: ["Steuer- und Sozialabgabenersparnis in der Ansparphase", "Mindestens 15 % Arbeitgeberzuschuss", "Mitnahme bei Jobwechsel", "Verschiedene Durchführungswege"],
    sections: [
      {
        heading: "So funktioniert die Entgeltumwandlung",
        paragraphs: [
          "Du wandelst einen Teil Deines Bruttogehalts in eine Betriebsrente um. Auf den umgewandelten Betrag fallen weder Lohnsteuer noch Sozialabgaben an – aus 100 € Bruttobeitrag werden netto oft nur 50–55 € Verzicht. Seit 2019 muss der Arbeitgeber zusätzlich mindestens 15 % Zuschuss leisten, sofern er Sozialversicherung spart.",
        ],
      },
      {
        heading: "Die fünf Durchführungswege",
        bullets: [
          { title: "Direktversicherung", text: "Häufigster Weg – Arbeitgeber schließt eine Versicherung ab, Du bist Begünstigte:r." },
          { title: "Pensionskasse", text: "Versicherungsähnliche Einrichtung mit oft günstigen Konditionen." },
          { title: "Pensionsfonds", text: "Höhere Aktienquoten und damit Renditechancen möglich." },
          { title: "Direktzusage", text: "Arbeitgeber zahlt direkt aus – nur bei großen Arbeitgebern üblich." },
          { title: "Unterstützungskasse", text: "Klassische Variante für Gehaltsumwandlung jenseits der Sozialversicherungs-Grenzen." },
        ],
      },
      {
        heading: "Worauf Du achten solltest",
        bullets: [
          { title: "Höhe des Arbeitgeberzuschusses", text: "Manche zahlen nur die Pflicht-15 %, andere bis zu 50 % – wichtigster Hebel." },
          { title: "Kostenquote des Tarifs", text: "Viele Rahmenverträge sind Altprodukte mit hohen Kosten – Wechsel in moderne Fondspolice oft sinnvoll." },
          { title: "Verbeitragung in der Rente", text: "GKV-Beiträge werden auf die Auszahlung fällig – im Vorfeld einplanen." },
          { title: "Mitnahme beim Arbeitgeberwechsel", text: "Vertrag sollte portabel sein – sonst Stillstand bei jedem Jobwechsel." },
        ],
      },
    ],
    notCovered: [
      "Beiträge oberhalb der steuerlichen Höchstgrenzen",
      "Vorzeitige Auszahlung vor Rentenbeginn",
      "Erbschaft an beliebige Begünstigte (Hinterbliebenenversorgung muss explizit vereinbart sein)",
    ],
    conclusion:
      "Wir prüfen den bestehenden Rahmenvertrag Deines Arbeitgebers, vergleichen die Konditionen und richten die bAV ggf. neu aus – mit dem Ziel maximaler Netto-Rente.",
  }),
  make({
    slug: "etf-sparplan",
    metaTitle: "ETF-Sparplan aufsetzen und Vermögen aufbauen | FINABLE",
    metaDescription: "ETF-Sparplan für langfristigen Vermögensaufbau: passende Broker vergleichen, Sparrate sinnvoll wählen und Dein Depot solide diversifiziert aufbauen.",
    name: "ETF-Sparplan & Depot",
    shortName: "ETF-Sparplan",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: TrendingUp,
    tagline: "Vermögensaufbau mit globalen Aktien – einfach und kostengünstig.",
    intro:
      "ETFs bilden ganze Märkte ab und sind ideal für langfristigen Vermögensaufbau. Schon 50 € im Monat reichen, um in tausende Unternehmen weltweit zu investieren – flexibel, transparent und kostengünstig.",
    forWhom: "Für alle, die langfristig Vermögen aufbauen wollen – ohne komplizierte Einzelaktien-Auswahl.",
    highlights: ["Schon ab 25 € monatlich möglich", "Breite Diversifikation weltweit", "Niedrige laufende Kosten", "Jederzeit verfügbar oder anpassbar"],
    sections: [
      {
        heading: "Warum ETFs?",
        bullets: [
          { title: "Diversifikation", text: "Mit einem einzigen ETF wie dem MSCI World investierst Du in über 1.500 Unternehmen aus 23 Industrieländern." },
          { title: "Niedrige Kosten", text: "Laufende Kosten (TER) liegen meist zwischen 0,07 % und 0,3 % p. a. – ein Bruchteil aktiver Fonds." },
          { title: "Transparenz", text: "Jederzeit klar, in welche Unternehmen Du investiert bist." },
          { title: "Flexibilität", text: "Sparrate erhöhen, pausieren oder Entnahme starten – jederzeit und gebührenfrei." },
        ],
      },
      {
        heading: "Bausteine einer guten Strategie",
        bullets: [
          { title: "Anlagehorizont", text: "ETFs spielen ihre Stärke ab ca. 10 Jahren aus – Schwankungen werden durch Zeit ausgeglichen." },
          { title: "Asset Allocation", text: "Mischung aus Aktien-, Anleihen- und ggf. Rohstoff-ETFs nach Risikoneigung." },
          { title: "Weltweit, breit, kostengünstig", text: "Kern-Satelliten-Strategie: 70–100 % MSCI World/All Country, ergänzt um EM, Small Caps oder Themen." },
          { title: "Thesaurierend vs. ausschüttend", text: "Thesaurierend = automatischer Zinseszinseffekt; ausschüttend = Cashflow auf dem Konto." },
          { title: "Sparerpauschbetrag nutzen", text: "1.000 € Freibetrag pro Jahr (2.000 € für Paare) gezielt mit ausschüttenden ETFs ausschöpfen." },
        ],
      },
      {
        heading: "Depot vs. Versicherungsmantel",
        paragraphs: [
          "Ein klassisches Online-Depot ist günstig und flexibel – allerdings fällt auf Erträge Abgeltungsteuer (25 % + Soli) an. Eine ETF-Fondspolice ist im Mantel steuerfrei und kann bei Auszahlung nach 12 Jahren / 62. Lebensjahr deutlich besser laufen. Wir zeigen Dir, wann welcher Weg passt.",
        ],
      },
    ],
    notCovered: [
      "Garantien auf Kursentwicklung oder Mindestrendite",
      "Sicherheit gegen kurzfristige Kursrückgänge",
      "Steuervorteile, sofern Depot statt Versicherungsmantel",
    ],
    conclusion:
      "Wir helfen Dir, ein passendes Depot zu eröffnen, eine Strategie aufzusetzen und die richtigen ETFs nach Deinen Zielen und Deiner Risikoneigung auszuwählen.",
  }),
  make({
    slug: "immobilien-kapitalanlage",
    metaTitle: "Immobilien als Kapitalanlage aufbauen | FINABLE",
    metaDescription: "Immobilien als Kapitalanlage: Rendite, Finanzierung und Steuerwirkung realistisch bewerten und Objekt sowie Kredit gemeinsam sauber strukturieren.",
    name: "Immobilien als Kapitalanlage",
    shortName: "Immobilien",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: Building2,
    tagline: "Sachwerte, die Mieteinnahmen und Steuervorteile vereinen.",
    intro:
      "Eine vermietete Immobilie kann gleichzeitig Vermögen aufbauen, monatliche Cashflows liefern und Steuervorteile bieten – richtig ausgewählt und finanziert. Ob Direktinvestment oder offene Immobilienfonds – wir zeigen Dir die passende Strategie.",
    forWhom: "Für Anleger:innen mit gutem Einkommen, soliden Eigenmitteln und einem Anlagehorizont von mindestens 10 Jahren.",
    highlights: ["Inflationsschutz durch Sachwerte", "Steuerliche AfA und Werbungskosten", "Mieteinnahmen als laufender Cashflow", "Hebelwirkung durch Finanzierung"],
    sections: [
      {
        heading: "Vorteile der Anlageklasse",
        bullets: [
          { title: "Sachwert mit Inflationsschutz", text: "Mieten und Immobilienwerte steigen langfristig mit der Inflation – realer Vermögenserhalt." },
          { title: "Cashflow durch Mieten", text: "Monatliche Einnahmen, die Tilgung und Kosten oft selbst tragen." },
          { title: "Hebelwirkung der Finanzierung", text: "Mit 20–30 % Eigenkapital kannst Du das Vielfache an Vermögen kontrollieren – die Bank arbeitet für Dich." },
          { title: "Steuerliche AfA", text: "2 % (Bestand) bzw. 3 % (Neubau ab 2023) jährlich abschreibbar – senkt die Steuerlast deutlich." },
        ],
      },
      {
        heading: "Worauf es bei der Auswahl ankommt",
        bullets: [
          { title: "Lage, Lage, Lage", text: "Mikro- und Makrolage entscheiden über Mietnachfrage, Wertentwicklung und Wiederverkäuflichkeit." },
          { title: "Realistische Mietrendite", text: "Bruttomietrendite über 4 % p. a. ist solide – darunter wird's eng mit dem Cashflow." },
          { title: "Bauqualität & Sanierungsstau", text: "Energiekonzept und Modernisierungsbedarf realistisch einplanen, sonst zerstören Sanierungen die Rendite." },
          { title: "Finanzierungsstruktur", text: "Lange Zinsbindung, ausreichende Tilgung, Sondertilgungsoptionen – die Hebelwirkung muss beherrschbar bleiben." },
        ],
      },
      {
        heading: "Direktinvestment oder Fonds?",
        bullets: [
          { title: "Eigentumswohnung als Kapitalanlage", text: "Maximale Steuervorteile und Cashflow, aber höchster Aufwand und Klumpenrisiko." },
          { title: "Offene Immobilienfonds", text: "Diversifiziert, liquide, geringere Rendite – sinnvoll als Beimischung." },
          { title: "Geschlossene Fonds / AIFs", text: "Höhere Renditechancen, aber unternehmerisches Risiko und lange Bindung." },
          { title: "REITs", text: "Börsennotierte Immobiliengesellschaften – jederzeit handelbar wie Aktien." },
        ],
      },
    ],
    notCovered: [
      "Garantierte Rendite oder Wertentwicklung",
      "Mietausfälle ohne separate Versicherung",
      "Schäden am Objekt (dafür: Wohngebäude- und Vermieter-Rechtsschutz)",
    ],
    conclusion:
      "Wir prüfen Standorte, Renditen und Finanzierungsstruktur und arbeiten nur mit Bestandsobjekten oder Bauträgern, die wir selbst kennen.",
  }),

  // ─────────────── Gewerbeversicherung ───────────────
  make({
    slug: "betriebshaftpflichtversicherung",
    metaTitle: "Betriebshaftpflichtversicherung vergleichen | FINABLE",
    metaDescription: "Betriebshaftpflicht für Dein Unternehmen: Personen-, Sach- und Vermögensschäden zuverlässig absichern und branchenpassende Tarife unabhängig vergleichen.",
    name: "Betriebs­haftpflicht­versicherung",
    shortName: "Betriebshaftpflicht",
    category: "Gewerbeversicherung",
    icon: ShieldAlert,
    tagline: "Die Privathaftpflicht für Dein Unternehmen.",
    intro:
      "Sobald Du gewerblich tätig bist, ersetzt die Betriebshaftpflicht die private Haftpflicht für betrieblich verursachte Schäden. Sie ist für nahezu jedes Unternehmen unverzichtbar – vom Handwerksbetrieb bis zur Agentur.",
    forWhom: "Für alle Selbstständigen, Freiberufler:innen und Unternehmen.",
    highlights: ["Personen-, Sach- und Vermögensfolgeschäden", "Branchenspezifische Bausteine", "Schutz für Mitarbeitende", "Tätigkeitsschäden inklusive"],
    sections: [
      {
        heading: "Was abgesichert ist",
        bullets: [
          { title: "Personenschäden", text: "Wenn ein Kunde in Deinen Räumen stürzt oder ein Mitarbeiter:in jemanden verletzt – die Versicherung zahlt Behandlungs- und Schmerzensgeldkosten." },
          { title: "Sachschäden", text: "Beschädigte Kundengeräte, fremdes Eigentum auf der Baustelle oder beim Service-Einsatz beim Kunden." },
          { title: "Vermögensfolgeschäden", text: "Finanzielle Folgen aus einem Personen- oder Sachschaden – z. B. wenn Dein Schaden den Betrieb des Kunden lahmlegt." },
          { title: "Produkthaftpflicht", text: "Schäden, die durch Deine Produkte oder hergestellte Werke beim Endkunden entstehen." },
        ],
      },
      {
        heading: "Wichtige Bausteine je nach Branche",
        bullets: [
          { title: "Bearbeitungsschäden", text: "Beschädigung an Werkstücken, an denen Du gerade arbeitest – essenziell für Handwerk und Reparaturbetriebe." },
          { title: "Tätigkeitsschäden", text: "Schäden an fremden Sachen, die unmittelbar durch Deine Tätigkeit entstehen." },
          { title: "Schlüsselverlust", text: "Übernahme der Kosten für neue Schließanlagen bei Kundenobjekten." },
          { title: "Mietsachschäden", text: "Schäden an gemieteten Geschäftsräumen – wichtig für alle, die nicht im Eigentum arbeiten." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzlich herbeigeführte Schäden",
      "Reine Erfüllungsschäden (mangelhafte Lieferung als solche)",
      "Schäden an eigenen Sachen",
      "Vermögensschäden aus Beratung – dafür gibt es die Vermögensschadenhaftpflicht",
    ],
    conclusion:
      "Wir wählen den Tarif, der genau zu Deiner Branche passt – inklusive sinnvoller Bausteine wie Schlüsselverlust, Bearbeitungsschäden und erweiterter Produkthaftpflicht.",
  }),
  make({
    slug: "vermoegensschadenhaftpflicht",
    metaTitle: "Vermögensschadenhaftpflicht absichern | FINABLE",
    metaDescription: "Vermögensschadenhaftpflicht für Beratungs- und Planungsberufe: reine Vermögensschäden aus Beratungsfehlern absichern und passende Deckung wählen.",
    name: "Vermögens­schaden­haftpflicht",
    shortName: "Vermögensschadenhaftpflicht",
    category: "Gewerbeversicherung",
    icon: Coins,
    tagline: "Schutz bei Beratungsfehlern und reinen Vermögensschäden.",
    intro:
      "Beratende Berufe haften für Fehler, die ausschließlich finanzielle Schäden verursachen. Die Vermögensschadenhaftpflicht übernimmt diese Forderungen – für viele Berufsgruppen wie Steuerberater:innen, Anwält:innen oder Versicherungsmakler:innen sogar Pflicht.",
    forWhom: "Für beratende und planende Berufe sowie Unternehmen mit hoher wirtschaftlicher Verantwortung.",
    highlights: ["Hohe Versicherungssummen", "Berufsspezifische Tarife", "Tätigkeit im EU-Ausland mitversicherbar", "Rechtsverteidigung inklusive"],
    sections: [
      {
        heading: "Was ein Vermögensschaden ist",
        paragraphs: [
          "Ein reiner Vermögensschaden entsteht ohne vorherigen Personen- oder Sachschaden – z. B. wenn ein Steuerberater eine Frist verpasst, eine Architektin sich verkalkuliert oder ein Versicherungsmakler eine Lücke übersieht. Solche Schäden sind in der normalen Betriebshaftpflicht ausgeschlossen – die Vermögensschadenhaftpflicht greift gezielt diese Risiken auf.",
        ],
      },
      {
        heading: "Pflichtversicherung für viele Berufe",
        bullets: [
          { title: "Steuerberater:innen & Wirtschaftsprüfer:innen", text: "Mindestsumme 250.000 € pro Versicherungsfall, oft deutlich höher gewählt." },
          { title: "Rechtsanwält:innen", text: "Mindestens 250.000 € pro Versicherungsfall – höhere Summen bei wirtschaftsrechtlicher Beratung." },
          { title: "Versicherungsmakler:innen / Finanzanlagenvermittler:innen", text: "Mindestsumme 1,3 Mio. € pro Versicherungsfall, gesetzlich vorgeschrieben." },
          { title: "Architekt:innen & Ingenieur:innen", text: "Bauplanungs- und Statikfehler können Millionenschäden verursachen." },
        ],
      },
      {
        heading: "Worauf wir bei Tarifen achten",
        bullets: [
          { title: "Passive Rechtsschutzfunktion", text: "Versicherer wehrt unberechtigte Forderungen auf eigene Kosten ab." },
          { title: "Tätigkeitsumfang sauber definiert", text: "Alle Beratungs- und Nebentätigkeiten müssen ausdrücklich versichert sein." },
          { title: "Auslandsklausel", text: "Beratungen für ausländische Mandanten oder Reisen ins Ausland einschließen." },
          { title: "Nachhaftung", text: "Auch nach Mandatsende oder Praxisaufgabe sollten Forderungen noch gedeckt sein – idealerweise unbegrenzt." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzliche Pflichtverletzungen",
      "Schäden, die schon vor Vertragsbeginn eintraten",
      "Tätigkeiten außerhalb des versicherten Berufsbildes",
      "Vertragsstrafen und Bußgelder",
    ],
    conclusion:
      "Wir vergleichen Tarife für Deine Berufsgruppe und stellen sicher, dass alle relevanten Tätigkeiten und Auslandsbezüge sauber abgedeckt sind.",
  }),
  make({
    slug: "firmenrechtsschutz",
    metaTitle: "Firmenrechtsschutz für Unternehmen | FINABLE",
    metaDescription: "Firmenrechtsschutz für Unternehmen und Selbstständige: Arbeits-, Vertrags- und Steuerrecht absichern und passende Bausteine unabhängig vergleichen.",
    name: "Firmen-Rechtsschutz",
    shortName: "Firmenrechtsschutz",
    category: "Gewerbeversicherung",
    icon: Scale,
    tagline: "Rechtsbeistand für Unternehmen.",
    intro:
      "Vom Streit mit Mitarbeitenden über Forderungen aus Verträgen bis hin zu Steuerverfahren: Der Firmen-Rechtsschutz übernimmt Anwalts- und Gerichtskosten und sichert Deine wirtschaftliche Handlungsfähigkeit.",
    forWhom: "Für KMU, Handwerksbetriebe und Freiberufler:innen.",
    highlights: ["Arbeits- und Vertragsrecht", "Steuer- und Sozialgerichtsverfahren", "Strafrechtsschutz", "Rechtshotline für den Alltag"],
    sections: [
      {
        heading: "Bausteine im Überblick",
        bullets: [
          { title: "Arbeitsrecht", text: "Streitigkeiten mit Mitarbeitenden, Kündigungsschutzklagen, Abfindungsverhandlungen." },
          { title: "Vertragsrecht", text: "Forderungen aus Liefer-, Dienstleistungs- oder Werkverträgen einklagen oder abwehren." },
          { title: "Steuerrecht", text: "Verfahren vor Finanzgerichten – wichtig bei strittigen Bescheiden oder Betriebsprüfungen." },
          { title: "Sozialgerichtsverfahren", text: "Streit mit Berufsgenossenschaft oder Krankenkasse über Beitragsbescheide." },
          { title: "Strafrechtsschutz", text: "Verteidigung bei betrieblichen Vorwürfen wie z. B. Steuerstraftaten oder Verstößen gegen das Lebensmittelrecht." },
          { title: "Daten- & Cyberrechtsschutz", text: "Verfahren rund um DSGVO-Verstöße, Datenklau oder Aufsichtsbehörden." },
        ],
      },
      {
        heading: "Sinnvolle Zusatzleistungen",
        bullets: [
          { title: "Telefonische Rechtshotline", text: "Schnelle Erstberatung im Tagesgeschäft – oft ohne Selbstbeteiligung." },
          { title: "Mediation", text: "Außergerichtliche Konfliktlösung mit Kunden, Lieferanten oder Mitarbeitenden." },
          { title: "Forderungs­management", text: "Unterstützung beim Inkasso unbezahlter Rechnungen." },
          { title: "Hohe Deckungssumme", text: "Mindestens 500.000 € pro Fall – mehrere Instanzen und Sachverständige werden teuer." },
        ],
      },
    ],
    notCovered: [
      "Streitigkeiten mit Mitversicherten innerhalb des Unternehmens",
      "Vorsätzliche Straftaten",
      "Bereits laufende oder vor Vertragsbeginn bekannte Verfahren",
      "Kartell- und Patentrecht in der Regel ausgeschlossen",
    ],
    conclusion:
      "Wir konfigurieren Deinen Firmen-Rechtsschutz nach Größe, Branche und Risikoprofil – inkl. Datenschutz- und Cyberrechts­bausteinen.",
  }),
  make({
    slug: "gewerbliche-gebaeudeversicherung",
    metaTitle: "Gewerbliche Gebäudeversicherung | FINABLE",
    metaDescription: "Gewerbliche Gebäudeversicherung für Deine Betriebsimmobilie: Feuer, Sturm, Leitungswasser und Elementarschäden gezielt und zum Neuwert absichern.",
    name: "Gewerbliche Gebäude­versicherung",
    shortName: "Gewerbe-Gebäude",
    category: "Gewerbeversicherung",
    icon: Building2,
    tagline: "Schutz für Geschäfts- und Produktionsgebäude.",
    intro:
      "Brand, Sturm, Leitungswasser oder Vandalismus: Die gewerbliche Gebäudeversicherung sichert Deine Immobilie inklusive fest verbauter Anlagen ab und übernimmt im Schadenfall auch den Mietausfall.",
    forWhom: "Für Eigentümer:innen gewerblich genutzter Immobilien sowie Vermieter:innen.",
    highlights: ["Versicherung zum gleitenden Neuwert", "Mietausfallentschädigung", "Elementarschäden mitversicherbar", "Photovoltaik einschließbar"],
    sections: [
      {
        heading: "Versicherte Bausubstanz",
        paragraphs: [
          "Mauerwerk, Dach, fest verbaute haustechnische Anlagen, Aufzüge, Photovoltaikanlagen, Werbetafeln und Nebengebäude wie Lagerhallen oder Carports. Auf Wunsch werden auch Außenanlagen, Pflasterungen und Einfriedungen mitversichert.",
        ],
      },
      {
        heading: "Versicherte Gefahren",
        bullets: [
          { title: "Feuer", text: "Brand, Blitzschlag, Explosion, Implosion sowie Anprallschäden." },
          { title: "Leitungswasser", text: "Rohrbruch, Frost und Folgeschäden – auch an Sprinkleranlagen." },
          { title: "Sturm & Hagel", text: "Ab Windstärke 8 inkl. Folgeschäden durch eindringendes Wasser." },
          { title: "Mietausfall", text: "Bis zu 36 Monate, wenn das Objekt nach Schaden unbewohnbar wird." },
          { title: "Vandalismus & Einbruch", text: "Schäden an Türen, Fenstern und fest verbauten Anlagen." },
        ],
      },
      {
        heading: "Wichtige Erweiterungen",
        bullets: [
          { title: "Elementarschäden", text: "Hochwasser, Starkregen, Rückstau, Erdrutsch – angesichts steigender Risiken nahezu Pflicht." },
          { title: "Photovoltaik-Klausel", text: "Schäden an Modulen, Wechselrichtern und Mindererträge bei Ausfall." },
          { title: "Glasbruch", text: "Schaufenster und große Verglasungen – wichtig für Ladenlokale und Showrooms." },
          { title: "Unbenannte Gefahren / Allgefahrendeckung", text: "Schutz auch für Gefahren, die nicht ausdrücklich aufgelistet sind." },
        ],
      },
    ],
    notCovered: [
      "Inneneinrichtung & Geschäftsausstattung – dafür Inhaltsversicherung",
      "Schäden durch unterlassene Instandhaltung",
      "Schäden, die schon bei Vertragsbeginn vorhanden waren",
    ],
    conclusion:
      "Wir analysieren das Risikoprofil Deines Objekts und stellen den Versicherungsschutz auf eine solide, lückenlose Basis.",
  }),
  make({
    slug: "elektronikversicherung",
    metaTitle: "Elektronikversicherung für Betriebe | FINABLE",
    metaDescription: "Elektronikversicherung für IT und Geräte im Betrieb: Diebstahl, Bedienfehler und technische Schäden zum Neuwert absichern und Betriebsausfall vermeiden.",
    name: "Elektronik­versicherung",
    shortName: "Elektronik",
    category: "Gewerbeversicherung",
    icon: Cpu,
    tagline: "Allgefahrenschutz für Deine Technik.",
    intro:
      "Server, Laptops, medizinische Geräte oder Maschinensteuerungen sind das Herzstück moderner Betriebe. Die Elektronikversicherung deckt nahezu alle Beschädigungs- und Diebstahlrisiken zum Neuwert ab.",
    forWhom: "Für Praxen, IT-Unternehmen, Produktionsbetriebe und alle, die auf hochwertige Elektronik angewiesen sind.",
    highlights: ["Allgefahrendeckung (Allrisk)", "Neuwertentschädigung", "Schutz für mobile Geräte", "Datenwiederherstellung möglich"],
    sections: [
      {
        heading: "Was abgesichert ist",
        bullets: [
          { title: "Bedienungsfehler & Ungeschicklichkeit", text: "Heruntergefallene Laptops, vergossener Kaffee – auch fahrlässig verursachte Schäden." },
          { title: "Brand, Wasser, Sturm", text: "Klassische Elementargefahren am Standort." },
          { title: "Diebstahl & Einbruchdiebstahl", text: "Inkl. Raub und einfachem Diebstahl, wenn vereinbart." },
          { title: "Überspannung & Kurzschluss", text: "Auch Schäden durch Blitz oder fehlerhafte Stromversorgung." },
          { title: "Konstruktions- & Materialfehler", text: "Außerhalb der Herstellergarantie wertvoll." },
        ],
      },
      {
        heading: "Sinnvolle Bausteine",
        bullets: [
          { title: "Mobile Elektronik", text: "Laptops, Tablets, Vermessungsgeräte – weltweit unterwegs versichert." },
          { title: "Datenträgerversicherung", text: "Kosten für Wiederherstellung von Daten nach Hardware-Defekt." },
          { title: "Software-Wiederherstellung", text: "Lizenzgebühren und Installationskosten für betriebsnotwendige Software." },
          { title: "Mehrkosten-Versicherung", text: "Übernahme von Miet-, Express- oder Reisekosten zur Aufrechterhaltung des Betriebs." },
        ],
      },
    ],
    notCovered: [
      "Verschleiß und altersbedingte Mängel",
      "Schäden, für die der Hersteller einzustehen hat",
      "Vorsätzlich verursachte Schäden",
      "Datenverlust durch reine Software-/Bedienungsfehler ohne Hardwareschaden",
    ],
    conclusion:
      "Wir konfigurieren den Schutz passgenau – inklusive Software, Datenträger und Mehrkosten bei Ersatzbeschaffung.",
  }),
  make({
    slug: "maschinenbruchversicherung",
    metaTitle: "Maschinenbruchversicherung vergleichen | FINABLE",
    metaDescription: "Maschinenbruchversicherung für Produktion und Handwerk: plötzliche Schäden an Maschinen absichern, Reparaturkosten abfedern und Ausfallzeiten reduzieren.",
    name: "Maschinen­bruch­versicherung",
    shortName: "Maschinenbruch",
    category: "Gewerbeversicherung",
    icon: Wrench,
    tagline: "Wenn Produktionsanlagen ausfallen, zählt jede Minute.",
    intro:
      "Plötzlich auftretende Schäden an Maschinen können Produktionen lahmlegen und massive Folgekosten verursachen. Die Maschinenbruchversicherung übernimmt Reparatur und Wiederbeschaffung – auch durch Bedienungsfehler oder Materialermüdung.",
    forWhom: "Für produzierende Unternehmen, Handwerksbetriebe und alle mit komplexen Anlagen.",
    highlights: ["Schäden durch Fehlbedienung", "Konstruktions- und Materialfehler", "Kurzschluss und Überspannung", "Optional: Betriebsunterbrechung"],
    sections: [
      {
        heading: "Was abgesichert ist",
        bullets: [
          { title: "Bedienungsfehler", text: "Schäden durch Mitarbeitende, die eine Anlage falsch ansteuern – einer der häufigsten Schadensursachen." },
          { title: "Konstruktions-, Material- und Ausführungsfehler", text: "Wenn Garantie oder Gewährleistung nicht (mehr) greifen." },
          { title: "Kurzschluss, Überstrom, Überspannung", text: "Schäden an Steuerungen und elektronischen Bauteilen." },
          { title: "Bruch durch Fliehkraft", text: "Materialermüdung in Wellen, Lagern und rotierenden Teilen." },
          { title: "Versagen von Mess-, Regel- und Sicherheitseinrichtungen", text: "Folgeschäden durch Sensorausfälle oder Fehlsteuerungen." },
        ],
      },
      {
        heading: "Sinnvolle Erweiterungen",
        bullets: [
          { title: "Maschinen-Betriebsunterbrechung", text: "Ersatz des entgangenen Gewinns, solange die Anlage repariert wird." },
          { title: "Mehrkosten-Versicherung", text: "Übernahme von Express-, Mietmaschinen- oder Auslagerungskosten." },
          { title: "Selbstfahrende Arbeitsmaschinen", text: "Bagger, Kräne und Stapler können in einer Police gebündelt werden." },
        ],
      },
    ],
    notCovered: [
      "Verschleiß und Alterung",
      "Schäden, für die der Hersteller einsteht (Garantie, Gewährleistung)",
      "Vorsätzlich herbeigeführte Schäden",
      "Reine Funktionsstörungen ohne Sachschaden",
    ],
    conclusion:
      "Wir bewerten die Risiken jeder einzelnen Anlage und stimmen den Versicherungsschutz mit dem Maschinenpark ab.",
  }),
  make({
    slug: "it-haftpflichtversicherung",
    metaTitle: "IT-Haftpflichtversicherung für IT-Berufe | FINABLE",
    metaDescription: "IT-Haftpflicht für IT-Dienstleister und Entwickler: Vermögensschäden aus Beratung, Programmierung und Betrieb absichern und Deckung passgenau wählen.",
    name: "IT-Haftpflicht­versicherung",
    shortName: "IT-Haftpflicht",
    category: "Gewerbeversicherung",
    icon: Server,
    tagline: "Branchenspezifischer Schutz für IT-Dienstleister.",
    intro:
      "Die IT-Haftpflicht kombiniert Betriebs- und Vermögensschadenhaftpflicht – speziell zugeschnitten auf Software-Entwicklung, Beratung, Hosting und SaaS. Damit ein vergessener Patch oder ein Bug nicht zur Existenzfrage wird.",
    forWhom: "Für IT-Dienstleister, Agenturen, SaaS-Anbieter und Freelancer:innen.",
    highlights: ["Vermögens- und Sachschäden in einer Police", "Schutz bei Datenverlust und Hacking", "Tätigkeit weltweit mitversicherbar", "Pflichtverletzungen aus Verträgen"],
    sections: [
      {
        heading: "Typische Risiken in der IT",
        bullets: [
          { title: "Programmierfehler", text: "Bug im Code legt Kunden-Webshop lahm – Umsatzverlust wird Dir in Rechnung gestellt." },
          { title: "Beratungsfehler", text: "Empfehlung einer ungeeigneten Architektur, die später teuer migriert werden muss." },
          { title: "Datenverlust beim Kunden", text: "Falscher SQL-Befehl löscht produktive Datenbank – Wiederherstellung kostet sechsstellig." },
          { title: "Verletzung von Schutzrechten", text: "Versehentliche Nutzung urheberrechtlich geschützter Bibliotheken oder Designs." },
          { title: "Cyber- & Hacking-Folgen", text: "Übernahme von Schadenersatzforderungen, wenn Deine Software Einfallstor war." },
        ],
      },
      {
        heading: "Was die Police leistet",
        bullets: [
          { title: "Vermögensschadenhaftpflicht", text: "Reine Vermögensschäden ohne vorherigen Personen-/Sachschaden – Kernstück für IT-Dienstleister." },
          { title: "Betriebshaftpflicht", text: "Personen- und Sachschäden vor Ort beim Kunden oder im eigenen Büro." },
          { title: "Tätigkeitsschäden", text: "Beschädigung an Systemen, an denen Du gerade arbeitest." },
          { title: "Weltweite Deckung", text: "Wichtig für SaaS-Anbieter und Cloud-Dienste mit internationalen Kunden." },
        ],
      },
      {
        heading: "Worauf wir bei der Tarifwahl achten",
        bullets: [
          { title: "Tätigkeits- und Branchenklausel passend", text: "Software-Entwicklung, Consulting, Hosting, SaaS – muss explizit aufgeführt sein." },
          { title: "Versicherungssumme angemessen", text: "Bei B2B-Verträgen mit größeren Kunden mind. 1–3 Mio. €." },
          { title: "Vertragsstrafen einschließbar", text: "Wenn SLAs vereinbart sind, sollten daraus resultierende Forderungen mitversichert sein." },
          { title: "Cyber-Bausteine", text: "Idealerweise Kombination mit Cyber-Versicherung über denselben Versicherer." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzliche Pflichtverletzungen",
      "Schäden, die schon vor Vertragsbeginn bekannt waren",
      "Reine Vertragserfüllung (Nachbesserung des eigenen Werks)",
      "Bußgelder und Strafen außerhalb DSGVO",
    ],
    conclusion:
      "Wir kennen die spezifischen Risiken der IT-Branche und finden den Tarif, der zu Deinem Geschäftsmodell und Deinen Verträgen passt.",
  }),
  make({
    slug: "cyberversicherung",
    metaTitle: "Cyberversicherung für Unternehmen | FINABLE",
    metaDescription: "Cyberversicherung gegen Hackerangriffe und Datenverlust: Soforthilfe, Krisenmanagement und Haftpflicht in einem Baustein für Dein Unternehmen absichern.",
    name: "Cyber­versicherung",
    shortName: "Cyber",
    category: "Gewerbeversicherung",
    icon: Lock,
    tagline: "Schutz bei Hackerangriffen, Datenklau und Erpressung.",
    intro:
      "Ein erfolgreicher Cyber-Angriff kann ein Unternehmen tagelang lahmlegen, Lösegeldforderungen auslösen und massive Bußgelder nach DSGVO bedeuten. Die Cyberversicherung übernimmt Krisenmanagement, IT-Forensik, Wiederherstellung und Haftpflicht.",
    forWhom: "Für jedes Unternehmen, das digital arbeitet – also praktisch alle.",
    highlights: ["IT-Forensik & Krisenmanagement rund um die Uhr", "Lösegeldzahlungen bei Ransomware", "Datenschutz-Haftpflicht inklusive", "Betriebsunterbrechung durch Cybervorfall"],
    sections: [
      {
        heading: "Was abgesichert ist",
        bullets: [
          { title: "Cyber-Eigenschaden", text: "Wiederherstellung von Daten und Systemen, IT-Forensik, Hardware-Ersatz – alles, was nötig ist, um nach einem Angriff wieder arbeitsfähig zu werden." },
          { title: "Cyber-Haftpflicht", text: "Forderungen Dritter, deren Daten kompromittiert wurden – inklusive Anwalts- und Gerichtskosten." },
          { title: "Cyber-Betriebsunterbrechung", text: "Ersatz des entgangenen Gewinns, wenn der Betrieb durch einen Cybervorfall stillsteht." },
          { title: "Cyber-Erpressung", text: "Lösegeldzahlungen bei Ransomware sowie Honorare für spezialisierte Verhandler:innen." },
          { title: "Datenschutz-Bußgelder", text: "Übernahme von DSGVO-Bußgeldern, soweit gesetzlich versicherbar." },
        ],
      },
      {
        heading: "Typische Schadensszenarien",
        bullets: [
          { title: "Ransomware-Angriff", text: "Verschlüsselung Deiner Server – ohne Backup steht der Betrieb tagelang still." },
          { title: "Phishing & CEO-Fraud", text: "Mitarbeitende überweisen auf gefälschte Anweisung hohe Beträge an Betrüger." },
          { title: "Datenleck", text: "Kunden-, Mitarbeiter- oder Patientendaten gelangen ins Netz – Meldepflicht binnen 72 Stunden." },
          { title: "DDoS-Angriff", text: "Dein Online-Shop oder Deine Website ist nicht mehr erreichbar – Umsatz bricht weg." },
        ],
      },
    ],
    notCovered: [
      "Schäden durch vorsätzliches Handeln eigener Mitarbeitender",
      "Bußgelder aus rechtswidrigem Verhalten (außer DSGVO)",
      "Vor Vertragsabschluss bekannte Vorfälle",
      "Reine Reputationsschäden ohne finanzielle Forderung",
    ],
    conclusion:
      "Wir prüfen vorab Dein IT-Risikoprofil, beraten zu Mindeststandards (MFA, Backups, Patching) und finden den Tarif mit umfassender Krisenhilfe.",
  }),
  make({
    slug: "do-versicherung",
    metaTitle: "D&O-Versicherung für Manager & Geschäftsführer | FINABLE",
    metaDescription: "D&O-Versicherung für Geschäftsführer und Vorstände: persönliche Haftung aus Managemententscheidungen absichern und Deckungssumme sinnvoll bemessen.",
    name: "D&O-Versicherung",
    shortName: "D&O",
    category: "Gewerbeversicherung",
    icon: Briefcase,
    tagline: "Persönliche Haftung von Geschäftsführer:innen absichern.",
    intro:
      "Geschäftsführer:innen und Vorstände haften mit ihrem Privatvermögen unbegrenzt für Pflichtverletzungen. Die D&O-Versicherung schützt vor genau diesen Forderungen – sei es vom eigenen Unternehmen oder von Dritten.",
    forWhom: "Für Geschäftsführer:innen, Vorstände, Aufsichtsräte und Prokurist:innen.",
    highlights: ["Schutz des Privatvermögens", "Abwehr unberechtigter Ansprüche", "Innen- und Außenhaftung", "Auch nach Mandatsende noch Schutz"],
    sections: [
      {
        heading: "Warum Geschäftsführer:innen sie brauchen",
        paragraphs: [
          "Nach § 43 GmbHG haften Geschäftsführer:innen mit ihrem gesamten Privatvermögen unbegrenzt für Pflichtverletzungen. Schon eine fehlerhafte Investitionsentscheidung, ein verspätet gestellter Insolvenzantrag oder ein Verstoß gegen Compliance-Vorgaben können Forderungen in Millionenhöhe auslösen – auch Jahre später.",
        ],
      },
      {
        heading: "Innen- und Außenhaftung",
        bullets: [
          { title: "Innenhaftung", text: "Forderungen des eigenen Unternehmens (über die Gesellschafter) – mit Abstand der häufigste Schadenfall in der D&O." },
          { title: "Außenhaftung", text: "Forderungen Dritter – z. B. Banken, Insolvenzverwalter, Finanzamt, Lieferanten oder Kund:innen." },
        ],
      },
      {
        heading: "Worauf bei der Police zu achten ist",
        bullets: [
          { title: "Versicherungssumme", text: "Mindestens das ein- bis dreifache des Jahresumsatzes als Faustregel." },
          { title: "Selbstbehalt", text: "Pflicht-Selbstbehalt bei AGs (10 % des Schadens, max. 1,5x Jahresgehalt) – muss persönlich getragen werden." },
          { title: "Nachhaftung", text: "Forderungen können Jahre nach Mandatsende kommen – ideal sind 5–10 Jahre Nachhaftung, besser unbegrenzt." },
          { title: "Erweiterte Deckung", text: "Strafverteidigung, Reputationsschutz, PR-Krisenmanagement und Rechtsbeistand bei Untersuchungen." },
          { title: "Keine 'Insured-vs-Insured'-Klausel", text: "Auch Forderungen mitversicherter Personen untereinander sollten gedeckt sein." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzliche Pflichtverletzungen und Straftaten",
      "Bußgelder und Strafen, soweit gesetzlich nicht versicherbar",
      "Forderungen, die schon vor Vertragsbeginn bekannt waren",
      "Schäden aus wissentlichen Vertragsverletzungen",
    ],
    conclusion:
      "Wir analysieren Deinen Verantwortungsbereich, definieren die passende Versicherungssumme und gestalten den Vertrag so, dass auch das Privatvermögen langfristig geschützt bleibt.",
  }),
  make({
    slug: "berufshaftpflicht-aerzte",
    metaTitle: "Berufshaftpflicht für Ärzte & Heilberufe | FINABLE",
    metaDescription: "Berufshaftpflicht für Ärzte, Zahnärzte und Heilberufe: Behandlungsfehler und Personenschäden absichern und passende Deckungssumme fachlich wählen.",
    name: "Berufshaftpflicht für Ärzte",
    shortName: "Berufshaftpflicht Ärzte",
    category: "Gewerbeversicherung",
    icon: Stethoscope,
    tagline: "Pflichtschutz für Heilberufe.",
    intro:
      "Behandlungsfehler können zu hohen Schadenersatzforderungen führen. Die Berufshaftpflicht für Heilberufe ist Pflichtversicherung und schützt vor Personen-, Sach- und Vermögensschäden im Zuge der ärztlichen Tätigkeit.",
    forWhom: "Für Ärzt:innen, Zahnärzt:innen, Heilpraktiker:innen sowie therapeutische Berufe.",
    highlights: ["Hohe Versicherungssummen üblich", "Schutz für angestellte Ärzt:innen", "Belegarzttätigkeit einschließbar", "Schutz im EU-Ausland möglich"],
    sections: [
      {
        heading: "Pflicht für Heilberufe",
        paragraphs: [
          "Für niedergelassene Ärzt:innen, Zahnärzt:innen, Psychotherapeut:innen und Heilpraktiker:innen ist eine ausreichende Berufshaftpflicht in den meisten Bundesländern Pflicht. Sie schützt vor Schadenersatzforderungen aus Behandlungsfehlern – und schon ein einziger Fall kann sechs- bis siebenstellig werden.",
        ],
      },
      {
        heading: "Was abgesichert ist",
        bullets: [
          { title: "Personenschäden", text: "Behandlungsfehler mit körperlichen Folgen, Schmerzensgeld, Verdienstausfall und Pflegekosten." },
          { title: "Vermögensschäden", text: "Reine Vermögensschäden, etwa durch fehlerhafte Atteste oder Gutachten." },
          { title: "Sachschäden", text: "Beschädigung an Patienteneigentum (Brille, Hörgerät, Kleidung)." },
          { title: "Tätigkeit als Praxisinhaber:in", text: "Inkl. angestelltem Personal, Praxisvertretung und Honorarärzt:innen." },
        ],
      },
      {
        heading: "Wichtige Bausteine je nach Fachrichtung",
        bullets: [
          { title: "Belegarzt-Tätigkeit", text: "Operationen in Kliniken müssen ausdrücklich versichert sein." },
          { title: "IGeL-Leistungen", text: "Individuelle Gesundheitsleistungen separat einschließen." },
          { title: "Praxis-OP / ambulante Eingriffe", text: "Eigenes Risiko, das spezifisch versichert werden muss." },
          { title: "Lehrtätigkeiten & Gutachten", text: "Nebentätigkeiten gehören in den Tarif aufgenommen." },
          { title: "Auslandseinsätze", text: "Hilfsorganisationen, Kongresse, Fortbildungen – wichtig für mobile Tätigkeit." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzliche Pflichtverletzungen",
      "Behandlungen ohne erforderliche Approbation oder Erlaubnis",
      "Schönheitsoperationen ohne medizinische Indikation, sofern nicht ausdrücklich versichert",
      "Schäden aus Tätigkeit außerhalb des versicherten Berufsbildes",
    ],
    conclusion:
      "Wir vergleichen spezialisierte Tarife für Deine Fachrichtung und sorgen für lückenlosen Schutz – inklusive Nebentätigkeiten und Honorararztverträgen.",
  }),
  make({
    slug: "kfz-flottenversicherung",
    metaTitle: "Kfz-Flottenversicherung für Unternehmen | FINABLE",
    metaDescription: "Kfz-Flottenversicherung für Fuhrparks ab mehreren Fahrzeugen: einheitliche Deckung, bessere Konditionen und Verwaltung mit einem Ansprechpartner.",
    name: "KFZ-Flotten­versicherung",
    shortName: "KFZ-Flotte",
    category: "Gewerbeversicherung",
    icon: Truck,
    tagline: "Ein Vertrag für den gesamten Fuhrpark.",
    intro:
      "Statt für jedes Fahrzeug einzeln Tarife zu vergleichen, bündelst Du in der Flottenversicherung alle Fahrzeuge in einem Rahmenvertrag – mit individueller Beitragskalkulation auf Basis Deiner Schadenquote.",
    forWhom: "Für Unternehmen mit mehreren Fahrzeugen – ab ca. 5 Fahrzeugen meist deutlich günstiger.",
    highlights: ["Einheitliche Bedingungen für alle Fahrzeuge", "Individuelle Beitragskalkulation", "Vereinfachte Verwaltung", "Optimierter Schadenservice"],
    sections: [
      {
        heading: "Vorteile gegenüber Einzelpolicen",
        bullets: [
          { title: "Schadenquoten-basierte Prämie", text: "Statt SF-Klasse pro Fahrzeug zählt die Gesamtschadenquote – Top-Fahrer profitieren überdurchschnittlich." },
          { title: "Einheitliche Bedingungen", text: "Alle Fahrzeuge sind nach gleichen Konditionen versichert – kein Tarifdschungel." },
          { title: "Vereinfachte Verwaltung", text: "Ein Vertrag, ein Ansprechpartner, eine Rechnung – auch für Zu- und Abmeldungen." },
          { title: "Spezielle Schadenservices", text: "Direkte Anbindung an Werkstattnetzwerke, Mietwagenbereitstellung und Schadenmanagement-Tools." },
        ],
      },
      {
        heading: "Welche Fahrzeuge passen rein?",
        bullets: [
          { title: "PKW & Transporter", text: "Klassische Außendienst- und Lieferflotten." },
          { title: "LKW & Sattelzugmaschinen", text: "Spezialtarife mit Werkverkehr- oder Güterverkehrs-Klauseln." },
          { title: "Selbstfahrende Arbeitsmaschinen", text: "Bagger, Stapler, Kommunalfahrzeuge – meist mit reduzierter Beitragsstruktur." },
          { title: "Anhänger & Auflieger", text: "Auch ohne eigenen Antrieb in den Rahmenvertrag aufnehmbar." },
        ],
      },
      {
        heading: "Worauf wir bei Vertragsgestaltung achten",
        bullets: [
          { title: "Schadenfreiheits-Anteil", text: "Klare Regelung, wie Vorschadenfreiheit anerkannt wird – sonst überteuerter Einstieg." },
          { title: "Selbstbehalte differenziert", text: "Pro Fahrzeugtyp und Schadenart anpassen, nicht pauschal." },
          { title: "Auslandsdeckung", text: "Wichtig bei Speditionen oder Außendienst über Grenzen." },
          { title: "Telematik-Optionen", text: "Senken Beitrag und liefern Daten zur weiteren Optimierung." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzlich verursachte Schäden",
      "Schäden bei nicht zugelassenen Rennen",
      "Schäden unter Alkohol- oder Drogeneinfluss",
      "Verschleiß und Wartungskosten",
    ],
    conclusion:
      "Wir analysieren Deinen Fuhrpark und Deine Schadenhistorie und holen Angebote bei spezialisierten Flottenversicherern ein.",
  }),

  // ─────────────── Finanzierung ───────────────
  make({
    slug: "immobilienkredit",
    metaTitle: "Immobilienkredit vergleichen und finanzieren | FINABLE",
    metaDescription: "Immobilienkredit für Kauf, Neubau oder Anschlussfinanzierung: Zinsen unabhängig vergleichen und Finanzierung passend zu Deiner Situation aufstellen.",
    name: "Immobilien­finanzierung",
    shortName: "Immobilienkredit",
    category: "Finanzierung",
    icon: Home,
    tagline: "Top-Konditionen für Deinen Immobilientraum.",
    intro:
      "Eine Immobilienfinanzierung läuft 20–30 Jahre – kleine Zinsunterschiede bedeuten zehntausende Euro. Wir vergleichen über 400 Banken, optimieren Tilgung und Zinsbindung und sorgen dafür, dass Du Förderprogramme wie KfW oder BAFA optimal nutzt.",
    forWhom: "Für Käufer:innen von Eigenheimen, Anlageimmobilien sowie Anschluss- und Forward-Finanzierungen.",
    highlights: ["Vergleich von über 400 Banken", "Förderdarlehen integriert (KfW, BAFA)", "Zins- und Tilgungsoptimierung", "Forward-Darlehen bis 5 Jahre im Voraus"],
    sections: [
      {
        heading: "Wissenswertes zur Baufinanzierung",
        bullets: [
          { title: "Finanzierungsarten", text: "Annuitätendarlehen (gleichbleibende Raten), Volltilgerdarlehen (Tilgung bis Laufzeitende) oder Bauspardarlehen – wir wählen das Modell, das zu Deinem Lebensentwurf passt." },
          { title: "Zinsen & Zinsbindung", text: "Längere Zinsbindung gibt Planungssicherheit, ist aber meist etwas teurer. Aktuell sind 15–20 Jahre oft die beste Wahl." },
          { title: "Eigenkapital", text: "Mindestens 20 % Eigenkapital senken Risiko und Zinssatz. Auch Kaufnebenkosten (Notar, Grunderwerbsteuer, Makler – bis 15 %) sollten daraus gedeckt werden." },
          { title: "Tilgung", text: "Anfänglich 2–3 % Tilgung sind sinnvoll, um die Restschuld bei Auslauf der Zinsbindung niedrig zu halten." },
          { title: "Sondertilgungen", text: "Viele Banken erlauben kostenfreie Sondertilgungen – ein wichtiger Hebel, um schneller schuldenfrei zu werden." },
          { title: "Förderprogramme", text: "KfW-Darlehen, BAFA-Zuschüsse, Landesförderbanken (L-Bank, IBB, N-Bank …) und kommunale Programme können tausende Euro sparen." },
        ],
      },
      {
        heading: "Risiken und wie Du sie absicherst",
        bullets: [
          { title: "Zinsänderungsrisiko", text: "Steigende Zinsen nach Ablauf der Zinsbindung können die Rate massiv erhöhen. Lange Zinsbindungen oder Forward-Darlehen sichern Dich ab." },
          { title: "Hohe Restschuld", text: "Ohne ausreichende Tilgung wird die Anschlussfinanzierung zur Belastung. Wir kalkulieren Tilgung & Sondertilgung passend." },
          { title: "Einkommensverlust", text: "Arbeitslosigkeit oder Krankheit gefährden die Raten. Restkreditversicherung, Berufsunfähigkeitsversicherung oder Krankentagegeld können einspringen." },
          { title: "Tod oder Krankheit eines Partners", text: "Die Risikolebensversicherung sichert die Restschuld ab und schützt die Familie vor Zwangsverkauf." },
          { title: "Wertverlust der Immobilie", text: "Lage, Bauqualität und regelmäßige Instandhaltung sind die wichtigsten Hebel für Werterhalt." },
        ],
      },
      {
        heading: "Unser Vorgehen",
        paragraphs: [
          "Wir analysieren Deine Bonität, berechnen die maximal sinnvolle Finanzierungshöhe und vergleichen Konditionen bei über 400 Banken. Die finale Empfehlung ist immer transparent und berücksichtigt Förderdarlehen, Tilgungsstruktur und Anschlussstrategie.",
        ],
      },
    ],
    notCovered: [
      "Spekulative Finanzierungen ohne Eigenkapital",
      "Objekte ohne neutrale Wertindikation",
      "Beratung gegen Honorar – wir werden über die Bank vergütet",
    ],
    conclusion:
      "Wir begleiten Dich von der ersten Bonitätsprüfung bis zum Notartermin – unabhängig, transparent und mit den besten Konditionen am Markt.",
  }),
  make({
    slug: "konsumkredit",
    metaTitle: "Konsumkredit vergleichen und günstig finanzieren | FINABLE",
    metaDescription: "Konsumkredit für Auto, Umzug oder größere Anschaffung: Anbieter unabhängig vergleichen, effektiven Jahreszins prüfen und Laufzeit sinnvoll wählen.",
    name: "Konsumkredit",
    shortName: "Konsumkredit",
    category: "Finanzierung",
    icon: CreditCard,
    tagline: "Faire Kredite für Auto, Umbau oder Umschuldung.",
    intro:
      "Ob neuer Wagen, Renovierung oder Zusammenführung bestehender Kredite – wir vergleichen Banken und finden für Dich den Ratenkredit mit den besten Konditionen, ohne versteckte Kosten und mit fairer Restschuldversicherung (oder ohne, wenn unnötig).",
    forWhom: "Für alle, die einen größeren Anschaffungs- oder Umschuldungsbedarf haben.",
    highlights: ["Bonitätsabhängige Top-Zinsen", "Sondertilgung jederzeit möglich", "Restschuldversicherung optional", "Vergleich ohne SCHUFA-Eintrag"],
    sections: [
      {
        heading: "Worauf Du beim Konsumkredit achten solltest",
        bullets: [
          { title: "Effektiver Jahreszins vergleichen", text: "Nur der Effektivzins enthält alle Kosten und macht Angebote wirklich vergleichbar – nicht der beworbene Sollzins." },
          { title: "Laufzeit & Ratenhöhe", text: "Kürzere Laufzeit = weniger Zinsen, aber höhere Rate. Wir finden die Balance, die zu Deinem Budget passt." },
          { title: "Gesamtbetrag im Blick", text: "Achte auf die Summe, die Du am Ende zurückzahlst – nicht nur auf die monatliche Rate." },
          { title: "Flexibilität", text: "Sondertilgungen und vorzeitige Rückzahlung sollten kostenlos möglich sein." },
          { title: "Bonität & SCHUFA", text: "Deine Bonität bestimmt die Konditionen. Wir holen Angebote SCHUFA-neutral ein, damit Dein Score nicht leidet." },
          { title: "Restschuldversicherung kritisch prüfen", text: "Oft teuer und nur begrenzt sinnvoll. Wir empfehlen sie nur, wenn sie wirklich passt." },
        ],
      },
      {
        heading: "Typische Einsatzzwecke",
        bullets: [
          { title: "Auto- oder Motorradkauf", text: "Oft günstiger als die Händlerfinanzierung – inklusive Barzahler-Rabatt." },
          { title: "Renovierung & Modernisierung", text: "Schnelle Liquidität für Küche, Bad oder energetische Sanierung." },
          { title: "Umschuldung", text: "Mehrere teure Kredite oder ein Dispo werden zu einem günstigeren Kredit zusammengefasst – oft mit deutlicher Ersparnis." },
        ],
      },
    ],
    notCovered: [
      "Kredite ohne ausreichende Bonität",
      "Finanzierungen rein konsumtiver Wünsche, die Deine Haushaltsbilanz sprengen",
      "Kredite ohne SCHUFA über unseriöse Anbieter",
    ],
    conclusion:
      "Wir holen mehrere Angebote ein, ohne Deinen SCHUFA-Score zu belasten, und beraten Dich ehrlich, ob ein Kredit überhaupt sinnvoll ist.",
  }),

  // ─────────────── Neue Einträge ───────────────
  make({
    slug: "erwerbsminderungsversicherung",
    metaTitle: "Erwerbsminderungsversicherung – Alternative | FINABLE",
    metaDescription: "Erwerbsminderungsversicherung als BU-Alternative: Einkommen bei Erwerbsminderung absichern und leistungsstarke Tarife unabhängig vergleichen lassen.",
    name: "Erwerbsminderungs­versicherung",
    shortName: "Erwerbsminderung",
    category: "Einkommensversicherung",
    icon: Hourglass,
    tagline: "Wenn die Berufsunfähigkeitsversicherung nicht in Frage kommt – die Basisabsicherung der Arbeitskraft.",
    intro:
      "Die Erwerbsminderungsversicherung zahlt eine vereinbarte Rente, wenn Du aus gesundheitlichen Gründen gar keiner geregelten Tätigkeit auf dem allgemeinen Arbeitsmarkt mehr nachgehen kannst. Sie ist günstiger als eine Berufsunfähigkeitsversicherung – leistet aber erst bei vollständigem Verlust der Erwerbsfähigkeit.",
    forWhom:
      "Für alle, die keine bezahlbare Berufsunfähigkeitsversicherung bekommen (Vorerkrankungen, Risikoberufe) oder bewusst nur die Basis absichern wollen.",
    highlights: [
      "Günstiger als die Berufsunfähigkeitsversicherung",
      "Leistung bei voller Erwerbsminderung",
      "Auch bei Vorerkrankungen oft möglich",
      "Ergänzung zur gesetzlichen Erwerbsminderungsrente",
    ],
    sections: [
      {
        heading: "Unterschied zur Berufsunfähigkeitsversicherung",
        paragraphs: [
          "Die Berufsunfähigkeitsversicherung leistet, sobald Du Deinen zuletzt ausgeübten Beruf zu mindestens 50 % nicht mehr ausüben kannst. Die Erwerbsminderungsversicherung zahlt erst, wenn Du gar keiner Tätigkeit auf dem allgemeinen Arbeitsmarkt mehr nachgehen kannst – die Hürde ist deutlich höher.",
          "Dafür sind die Beiträge in der Regel 30–50 % günstiger und die Annahmequote auch bei Vorerkrankungen oder Risikoberufen besser.",
        ],
      },
      {
        heading: "Die gesetzliche Erwerbsminderungsrente",
        bullets: [
          { title: "Volle Erwerbsminderung", text: "Nur wer weniger als 3 Stunden täglich arbeiten kann, erhält die volle Rente – im Durchschnitt unter 1.000 € im Monat." },
          { title: "Teilweise Erwerbsminderung", text: "Bei 3–6 Stunden Restleistungsfähigkeit – meist nur die Hälfte der Vollrente." },
          { title: "Wartezeit 5 Jahre", text: "Mindestens 5 Jahre Beitragszahlung erforderlich – Berufsstarter:innen haben oft gar keinen Anspruch." },
        ],
      },
    ],
    notCovered: [
      "Berufsunfähigkeit, solange noch irgendeine Tätigkeit möglich ist",
      "Vor Antrag bekannte Erkrankungen ohne Angabe",
      "Vorsätzliche Herbeiführung",
    ],
    conclusion:
      "Die Erwerbsminderungsversicherung ist Plan B, wenn eine Berufsunfähigkeitsversicherung nicht möglich oder nicht bezahlbar ist. Wir prüfen anonym Deine Optionen und finden den passenden Tarif.",
  }),

  make({
    slug: "dienstunfaehigkeitsversicherung",
    metaTitle: "Dienstunfähigkeitsversicherung für Beamte | FINABLE",
    metaDescription: "Dienstunfähigkeitsversicherung für Beamte und Anwärter: DU-Klausel sinnvoll gestalten und Absicherung bei Dienstunfähigkeit unabhängig vergleichen.",
    name: "Dienst­unfähigkeits­versicherung",
    shortName: "Dienstunfähigkeit (DU)",
    category: "Einkommensversicherung",
    icon: BadgeCheck,
    tagline: "Die spezielle Absicherung für Beamt:innen.",
    intro:
      "Beamt:innen werden bei Dienstunfähigkeit in den Ruhestand versetzt – die Berechnung erfolgt nach speziellen Regeln, die eine normale Berufsunfähigkeitsversicherung oft nicht abbildet. Die DU-Klausel sorgt dafür, dass die Versicherung schon dann leistet, wenn der Dienstherr Dich für dienstunfähig erklärt.",
    forWhom:
      "Beamt:innen auf Probe, Lebenszeit und Widerruf sowie Beamtenanwärter:innen und Angestellte im öffentlichen Dienst mit Verbeamtungsperspektive.",
    highlights: [
      "Echte DU-Klausel statt Verweisung",
      "Schutz schon ab Beamt:in auf Widerruf",
      "Nahtloser Übergang zur Ruhegehaltsanspruch",
      "Wichtige Nachversicherungsgarantie bei Verbeamtung",
    ],
    sections: [
      {
        heading: "Warum eine reine Berufsunfähigkeitsversicherung für Beamt:innen oft nicht reicht",
        paragraphs: [
          "Wirst Du vom Dienstherrn als dienstunfähig eingestuft, heißt das nicht automatisch, dass Du auch berufsunfähig im Sinne der Bedingungen der Berufsunfähigkeitsversicherung bist. Ohne echte DU-Klausel kann der Versicherer die Leistung verweigern oder Dich auf andere Tätigkeiten verweisen.",
          "Eine spezielle Dienstunfähigkeitsversicherung – oder eine Berufsunfähigkeitsversicherung mit echter DU-Klausel – stellt sicher, dass die Rente fließt, sobald Dich Dein Dienstherr in den Ruhestand versetzt.",
        ],
      },
      {
        heading: "Echte vs. unechte DU-Klausel",
        bullets: [
          { title: "Echte DU-Klausel", text: "Versicherer erkennt die Entscheidung des Dienstherrn ohne eigene Prüfung an – das ist der Goldstandard." },
          { title: "Unechte DU-Klausel", text: "Versicherer behält sich eine eigene Prüfung vor – im Streitfall hilft sie wenig." },
          { title: "Spezialklauseln für Anwärter:innen", text: "Wichtig: Auch in der Probezeit sollte bei Dienstunfähigkeit gezahlt werden." },
        ],
      },
    ],
    notCovered: [
      "Vorsätzlich herbeigeführte Dienstunfähigkeit",
      "Verschwiegene Vorerkrankungen aus dem Antrag",
      "Dienstunfähigkeit infolge Krieg oder innerer Unruhen",
    ],
    conclusion:
      "Beamt:innen brauchen eine Police mit echter DU-Klausel. Wir vergleichen Tarife speziell für Anwärter:innen, Lehrer:innen, Polizei und Bundeswehr – schon ab dem ersten Tag der Verbeamtung.",
  }),

  make({
    slug: "riester-rente",
    metaTitle: "Riester-Rente – Bestand & Altersvorsorgedepot | FINABLE",
    metaDescription: "Riester-Rente prüfen: bestehende Verträge weiterführen oder ruhen stellen und Übergang zum geplanten Altersvorsorgedepot 2027 rechtzeitig planen.",
    name: "Riester-Rente",
    shortName: "Riester",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: Landmark,
    tagline: "Bestandsverträge optimieren – oder den Schritt zum Altersvorsorgedepot planen.",
    intro:
      "Die Riester-Rente bietet bis heute Zulagen und Steuervorteile – besonders für Familien und Geringverdiener:innen. Mit der Reform und dem Altersvorsorgedepot 2027 wird sie für Neuverträge unattraktiver. Bestandsverträge genießen Bestandsschutz und sollten geprüft, nicht voreilig gekündigt werden.",
    forWhom:
      "Für Familien mit Kindern, Geringverdiener:innen und Bestandskund:innen, die Zulagen und Steuervorteile weiter nutzen wollen.",
    highlights: [
      "Volle Zulagen bei 4 % Bruttoeinkommen Beitrag",
      "Kinderzulage 300 € pro Kind p. a.",
      "Steuerlich absetzbar bis 2.100 € p. a.",
      "Bestandsschutz für Altverträge",
    ],
    sections: [
      {
        heading: "So funktioniert die Förderung",
        bullets: [
          { title: "Grundzulage", text: "175 € pro Jahr für jede:n Sparer:in mit voller Beitragsleistung von 4 % des Bruttoeinkommens (max. 2.100 € abzgl. Zulagen)." },
          { title: "Kinderzulage", text: "300 € pro Kind und Jahr (geboren ab 2008) – wirkt sich besonders bei Familien stark aus." },
          { title: "Steuervorteil", text: "Beiträge inkl. Zulagen bis 2.100 € als Sonderausgaben absetzbar – bei hohen Steuersätzen lohnender als die reine Zulage." },
          { title: "Nachgelagerte Besteuerung", text: "Die Rente wird im Alter voll versteuert – meist zu einem niedrigeren Steuersatz." },
        ],
      },
      {
        heading: "Riester vs. Altersvorsorgedepot",
        paragraphs: [
          "Ab 2027 ersetzt das Altersvorsorgedepot die Riester-Rente bei Neuverträgen. Statt Garantieprodukten kannst Du in ETFs investieren – mit deutlich höheren Renditechancen und niedrigeren Kosten. Bestehende Riester-Verträge bleiben unter Bestandsschutz und können bespart werden.",
          "Wir prüfen Deinen Bestandsvertrag (Kosten, Garantieniveau, Fondsauswahl) und beraten, ob ein Beitragsfreistellung oder Wechsel ins neue Depot sinnvoll ist.",
        ],
      },
    ],
    notCovered: [
      "Auszahlung als Einmalkapital (nur bis 30 % möglich)",
      "Förderung bei Kapitalauszahlung – Zulagen müssen zurückgezahlt werden",
      "Vererbbarkeit an beliebige Erben",
    ],
    conclusion:
      "Wir analysieren Deinen Riester-Vertrag (Effektivkosten, Fondsanteil, Garantiekosten) und zeigen Dir, ob Weiterbesparen, Ruhendstellung oder Wechsel zum Altersvorsorgedepot die richtige Strategie ist.",
  }),

  make({
    slug: "altersvorsorgedepot-2027",
    metaTitle: "Altersvorsorgedepot – geförderte Altersvorsorge | FINABLE",
    metaDescription: "Altersvorsorgedepot als geplante Riester-Nachfolge: Förderung, Zulagen und Chancen frühzeitig verstehen und Deine Altersvorsorge neu strukturieren.",
    name: "Altersvorsorgedepot",
    shortName: "Altersvorsorgedepot",
    category: "Altersvorsorge & Vermögensaufbau",
    icon: LineChart,
    tagline: "Der Riester-Nachfolger – ETF-Sparen mit staatlicher Förderung.",
    intro:
      "Das Altersvorsorgedepot ist der ETF-basierte Nachfolger der Riester-Rente. Erstmals förderst Du Deine Altersvorsorge mit echten ETFs – ohne teure Garantien, ohne Versicherungsmantel, mit voller Renditechance des Kapitalmarkts. Wir bereiten Dich auf den Marktstart vor und prüfen, wann der Abschluss für Dich richtig ist.",
    forWhom:
      "Für alle förderberechtigten Sparer:innen (rentenversicherungspflichtige Angestellte, Beamt:innen, Eltern in Elternzeit) – besonders attraktiv für jüngere Generationen mit langem Anlagehorizont.",
    highlights: [
      "ETF-Sparen statt teurer Garantieprodukte",
      "Staatliche Zulagen erhalten bleiben",
      "Deutlich niedrigere Kosten als Riester",
      "Mehr Flexibilität bei Auszahlung",
    ],
    sections: [
      {
        heading: "Was das Altersvorsorgedepot ist",
        paragraphs: [
          "Das Altersvorsorgedepot ist ein staatlich gefördertes Depot, in dem Du gezielt in ETFs und Fonds investieren kannst. Anders als bei Riester gibt es keine Beitragsgarantie zum Rentenbeginn – dafür profitierst Du voll von der Aktienmarktrendite. Die Förderung erfolgt weiterhin über Zulagen und Steuervorteile.",
          "Damit schließt der Gesetzgeber die größte Schwäche der Riester-Rente: hohe Kosten und niedrige Renditen durch zwingende Beitragsgarantien.",
        ],
      },
      {
        heading: "Unterschiede zur Riester-Rente",
        bullets: [
          { title: "Keine Beitragsgarantie", text: "Du profitierst voll von Aktienmarktrenditen – über 30 Jahre statistisch deutlich höhere Endbeträge." },
          { title: "Niedrigere Kosten", text: "Bankdepot statt Versicherungsmantel – typische Kosten 0,2–0,5 % statt 1,5–2,5 % p. a." },
          { title: "Flexiblere Auszahlung", text: "Auszahlplan statt erzwungener lebenslanger Verrentung möglich." },
          { title: "Breitere ETF-Auswahl", text: "MSCI World, All Country, EM, Themen-ETFs – keine Beschränkung auf hauseigene Fonds." },
        ],
      },
      {
        heading: "Wann lohnt sich der Abschluss?",
        paragraphs: [
          "Sobald die Anbieter mit dem Marktstart ihre Tarife veröffentlichen, vergleichen wir Kostenquoten, ETF-Auswahl und Service. Für die meisten Förderberechtigten dürfte das neue Depot der Riester-Rente deutlich überlegen sein. Wer noch einen guten Riester-Bestandsvertrag hat, sollte den Wechsel individuell prüfen lassen.",
        ],
      },
    ],
    notCovered: [
      "Beitragsgarantie zum Rentenbeginn (entfällt bewusst)",
      "Förderung über die jährlichen Höchstgrenzen hinaus",
      "Kapitalauszahlung als Einmalsumme ohne Förderrückforderung",
    ],
    conclusion:
      "Wir verfolgen die Marktentwicklung und melden uns bei Dir, sobald die ersten Anbieter starten – mit einem objektiven Vergleich und einer Empfehlung, die zu Deinen Zielen passt.",
    badge: "Neu",
  }),

  make({
    slug: "pflegezusatzversicherung",
    metaTitle: "Pflegezusatzversicherung – Lücke schließen | FINABLE",
    metaDescription: "Pflegezusatzversicherung schließt die Lücke der gesetzlichen Pflegeversicherung: monatliches Pflegegeld sichern und Angehörige finanziell entlasten.",
    name: "Pflegezusatz­versicherung",
    shortName: "Pflegezusatz",
    category: "Gesundheitsvorsorge",
    icon: HandHeart,
    tagline: "Schließt die Lücke der gesetzlichen Pflegeversicherung.",
    intro:
      "Die gesetzliche Pflegeversicherung deckt nur einen Teil der tatsächlichen Pflegekosten – im Pflegegrad 5 bleiben oft mehr als 2.000 € pro Monat als Eigenanteil. Die Pflegezusatzversicherung schließt diese Lücke und schützt Dein Vermögen und das Deiner Angehörigen.",
    forWhom:
      "Für alle ab etwa 30 Jahren – je früher Du abschließt, desto günstiger der Beitrag und desto eher bist Du noch ohne Vorerkrankungen versicherbar.",
    highlights: [
      "Pflegetagegeld oder Pflegekostenversicherung",
      "Frei verfügbares Geld unabhängig von Belegen",
      "Staatliche Förderung (Pflege-Bahr) möglich",
      "Schutz vor dem Zugriff auf Angehörige",
    ],
    sections: [
      {
        heading: "Wie groß ist die Versorgungslücke wirklich?",
        paragraphs: [
          "Ein vollstationärer Pflegeplatz kostet je nach Region 3.500–5.500 € im Monat. Die gesetzliche Pflegeversicherung übernimmt im Pflegegrad 5 maximal 2.096 € – der Rest ist Eigenanteil. Reicht das Einkommen nicht, müssen erst Vermögen, dann unter Umständen Kinder einspringen.",
        ],
      },
      {
        heading: "Pflegetagegeld vs. Pflegekostenversicherung",
        bullets: [
          { title: "Pflegetagegeld", text: "Fester Tagessatz je Pflegegrad – frei verwendbar, ohne Belegnachweis. In der Praxis am beliebtesten." },
          { title: "Pflegekostenversicherung", text: "Erstattung tatsächlich entstandener Pflegekosten gegen Nachweis – meist mit höherer Kostenübernahme." },
          { title: "Pflegerentenversicherung", text: "Lebenslange Rente ab Pflegegrad – planbar, aber teurer und unflexibler." },
        ],
      },
      {
        heading: "Worauf wir bei Tarifen achten",
        bullets: [
          { title: "Leistung schon ab Pflegegrad 1", text: "Top-Tarife zahlen ab dem ersten Pflegegrad – nicht erst ab 3 oder 4." },
          { title: "Beitragsstabilität im Alter", text: "Anbieter mit solider Kalkulation und Altersrückstellungen wählen." },
          { title: "Keine erneute Gesundheitsprüfung", text: "Wichtig bei späterer Leistungserhöhung oder Tarifumstellung." },
          { title: "Dynamik", text: "Tagegeld sollte mit den steigenden Pflegekosten mitwachsen." },
        ],
      },
    ],
    notCovered: [
      "Vor Antrag bekannte Pflegebedürftigkeit",
      "Reine Altersbeschwerden ohne anerkannten Pflegegrad",
      "Leistung über das vereinbarte Tagegeld hinaus",
    ],
    conclusion:
      "Je früher Du abschließt, desto günstiger – ein 35-Jähriger zahlt für 50 € Tagegeld in Pflegegrad 5 oft 25–40 € im Monat. Wir vergleichen die Top-Tarife und finden den passenden Schutz.",
  }),

  make({
    slug: "auslandsreisekrankenversicherung",
    metaTitle: "Auslandsreisekrankenversicherung vergleichen | FINABLE",
    metaDescription: "Auslandsreisekrankenversicherung für Urlaub und Geschäftsreisen: medizinische Versorgung und Rücktransport weltweit für kleines Geld absichern lassen.",
    name: "Auslandsreise­kranken­versicherung",
    shortName: "Auslandsreisekranken",
    category: "Gesundheitsvorsorge",
    icon: Plane,
    tagline: "Sicher im Urlaub – schon ab wenigen Euro im Jahr.",
    intro:
      "Die gesetzliche Krankenversicherung leistet im Ausland oft nur eingeschränkt oder gar nicht – ein Krankenrücktransport aus Übersee kann schnell fünfstellig werden. Die Auslandsreisekrankenversicherung übernimmt diese Kosten weltweit und ist als Jahrespolice meist günstiger als jede Einzelreise-Versicherung.",
    forWhom:
      "Für alle, die mindestens einmal im Jahr ins Ausland reisen – ob Urlaub, Geschäftsreise oder Familienbesuch.",
    highlights: [
      "Weltweiter Schutz – meist ab 10–25 € pro Jahr",
      "Übernahme medizinisch sinnvoller Rücktransporte",
      "Familienverträge mit Kindern oft beitragsfrei",
      "Kein Selbstbehalt in Top-Tarifen",
    ],
    sections: [
      {
        heading: "Lücken der gesetzlichen Krankenversicherung",
        bullets: [
          { title: "Außerhalb der EU/EWR", text: "GKV zahlt im Regelfall gar nicht – Du trägst alle Kosten selbst." },
          { title: "Innerhalb der EU", text: "Nur Leistungen, die auch ein gesetzlich Versicherter im Reiseland bekäme – oft mit Eigenanteilen." },
          { title: "Rücktransport", text: "Wird von der GKV grundsätzlich nicht übernommen – auch bei medizinischer Notwendigkeit." },
          { title: "Privatkliniken & Spezialbehandlungen", text: "Im Ausland oft die einzige Option, aber nicht über GKV erstattungsfähig." },
        ],
      },
      {
        heading: "Worauf Du beim Tarif achten solltest",
        bullets: [
          { title: "Medizinisch sinnvoller Rücktransport", text: "Top-Tarife zahlen schon, wenn der Rücktransport medizinisch sinnvoll ist – nicht erst, wenn er zwingend notwendig ist." },
          { title: "Kein Selbstbehalt", text: "Gute Tarife verzichten auf jegliche Selbstbeteiligung." },
          { title: "Weltweite Geltung inkl. USA/Kanada", text: "Wichtig bei Fernreisen – Behandlungskosten dort enorm." },
          { title: "Reisedauer", text: "Standard ist 6–8 Wochen am Stück – für Langzeitreisen Spezialtarif." },
        ],
      },
    ],
    notCovered: [
      "Bereits vor Reiseantritt bestehende, instabile Erkrankungen",
      "Behandlungen, die Hauptzweck der Reise sind",
      "Zahnersatz und kieferorthopädische Maßnahmen",
      "Folgen vorsätzlicher Selbstgefährdung",
    ],
    conclusion:
      "Eine Jahrespolice für die ganze Familie kostet oft weniger als 30 € – wir empfehlen sie ausnahmslos jedem Reisenden.",
  }),

  make({
    slug: "fahrradversicherung",
    metaTitle: "Fahrradversicherung für Rad & E-Bike | FINABLE",
    metaDescription: "Fahrradversicherung für Fahrrad und E-Bike: Diebstahl, Vandalismus und Reparatur zum Neuwert absichern und passenden Tarif unabhängig vergleichen.",
    name: "Fahrrad­versicherung",
    shortName: "Fahrrad",
    category: "Sachversicherungen",
    icon: Bike,
    tagline: "Vollkasko-Schutz für Dein Rad oder E-Bike.",
    intro:
      "Hochwertige Fahrräder und E-Bikes kosten oft 2.000–6.000 €. Während die Hausratversicherung Diebstahl nur in begrenztem Umfang und nur am Versicherungsort abdeckt, bietet die Fahrradversicherung Vollkaskoschutz inklusive Akku, Verschleißteilen und Pannenhilfe.",
    forWhom:
      "Für Besitzer:innen hochwertiger Fahrräder, E-Bikes, Pedelecs und Lastenräder, die ihr Rad regelmäßig im öffentlichen Raum abstellen.",
    highlights: [
      "Diebstahl- und Vandalismusschutz 24/7",
      "Akku-Schutz bei E-Bikes",
      "Unfall- und Reparaturkostenschutz",
      "Pannenhilfe inklusive",
    ],
    sections: [
      {
        heading: "Unterschied zur Hausratversicherung",
        paragraphs: [
          "Die Hausratversicherung deckt Fahrraddiebstahl meist nur in Höhe von 1–10 % der Versicherungssumme und nur dann, wenn das Rad in einem abgeschlossenen Raum stand. Außerhalb (z. B. nachts an der Straße) greift sie häufig nicht oder verlangt eine Zusatzklausel.",
          "Eine spezialisierte Fahrradversicherung schützt Dich 24/7 weltweit – auch beim Einkaufen, im Café, im Bahnhof oder am Bahnhofsparker.",
        ],
      },
      {
        heading: "Top-Tarife leisten zusätzlich",
        bullets: [
          { title: "Reparaturkosten nach Unfall", text: "Eigene Stürze, Vandalismus, Wildunfälle – Werkstattkosten werden zum Neuwert übernommen." },
          { title: "Akku-Schutz", text: "Defekte oder gestohlene E-Bike-Akkus sind separat oft sehr teuer (600–1.500 €) – in guten Tarifen mit abgedeckt." },
          { title: "Verschleiß & Pannen", text: "Top-Tarife übernehmen auch Verschleißreparaturen sowie Pannenhilfe vor Ort." },
          { title: "Schutzbrief", text: "Pannen-Service, Übernachtung und Weitertransport bei Defekt." },
          { title: "Neuwertentschädigung", text: "In den ersten 3 Jahren wird der Neupreis ohne Zeitwertabzug erstattet." },
        ],
      },
      {
        heading: "Wann lohnt sich eine eigene Police?",
        paragraphs: [
          "Faustregel: Ab einem Radwert von rund 1.500 € oder bei jedem E-Bike. Eine Police kostet meist 4–8 % des Radwerts pro Jahr – bei E-Bikes und hochwertigen Rennrädern eine sinnvolle Investition.",
        ],
      },
    ],
    notCovered: [
      "Schäden durch grobe Fahrlässigkeit (z. B. unverschlossen abgestellt)",
      "Vorsätzlich herbeigeführte Schäden",
      "Renn- und Wettkampfveranstaltungen",
      "Verleih- und gewerblich genutzte Räder",
    ],
    conclusion:
      "Wir vergleichen Tarife mit Akku-Schutz, Reparaturkosten und Schutzbrief – und finden den passenden Schutz für Dein Rad oder E-Bike.",
  }),

  make({
    slug: "betriebsunterbrechungsversicherung",
    metaTitle: "Betriebsunterbrechungsversicherung | FINABLE",
    metaDescription: "Betriebsunterbrechungsversicherung sichert laufende Kosten und Gewinn nach Sach- oder IT-Schaden: passende Haftzeit und Deckungssumme festlegen.",
    name: "Betriebs­unterbrechungs­versicherung",
    shortName: "Betriebsunterbrechung",
    category: "Gewerbeversicherung",
    icon: AlertOctagon,
    tagline: "Wenn Dein Betrieb stillsteht, läuft Dein Einkommen weiter.",
    intro:
      "Ein Brand, Wasserschaden oder Cyber-Angriff kann Deinen Betrieb wochenlang lahmlegen. Während die Sachversicherung den Schaden repariert, kommen weiter Fixkosten und entgangener Gewinn auf Dich zu. Die Betriebsunterbrechungs­versicherung übernimmt diese laufenden Kosten und sichert Dein Einkommen.",
    forWhom:
      "Für Selbstständige, Freiberufler:innen und KMU – besonders wichtig für Praxen, Gastronomie, Produktionsbetriebe und Einzelhandel mit hohen Fixkosten.",
    highlights: [
      "Ersatz von fortlaufenden Kosten und Gewinn",
      "Kombinierbar mit Sach- oder Cyber-Police",
      "Haftzeit meist 12 bis 24 Monate",
      "Mehrkosten zur schnellen Wiederaufnahme",
    ],
    sections: [
      {
        heading: "Was die Versicherung leistet",
        bullets: [
          { title: "Fortlaufende Kosten", text: "Miete, Gehälter, Leasingraten, Versicherungen, Zinsen – alle Fixkosten laufen weiter und werden übernommen." },
          { title: "Entgangener Gewinn", text: "Der Betriebsgewinn, der ohne Schaden erwirtschaftet worden wäre, wird erstattet." },
          { title: "Mehrkosten", text: "Aushilfslokale, Express-Lieferungen oder Auslagerungen zur schnelleren Wiederaufnahme des Betriebs." },
          { title: "Haftzeit", text: "Üblich sind 12–24 Monate – ausreichend, um auch nach einem Totalschaden wieder produktionsfähig zu sein." },
        ],
      },
      {
        heading: "Unterschied zur Betriebshaftpflicht",
        paragraphs: [
          "Die Betriebshaftpflicht zahlt für Schäden, die Du anderen zufügst. Die Betriebsunterbrechungs­versicherung zahlt Dir selbst – wenn Dein Betrieb durch einen versicherten Sachschaden, Cyber-Vorfall oder Maschinenausfall stillsteht. Beide Policen sind nicht austauschbar, sondern ergänzen sich.",
        ],
      },
      {
        heading: "Typische Auslöser",
        bullets: [
          { title: "Brand & Leitungswasser", text: "Klassische Sach-Betriebsunterbrechung – meist an die Inhalts- oder Gebäudeversicherung gekoppelt." },
          { title: "Maschinenbruch", text: "Defekte Produktionsanlagen – Maschinen-Betriebsunterbrechung als Ergänzung zur Maschinenbruchpolice." },
          { title: "Cyber-Vorfall", text: "Verschlüsselte Server, Ausfall des Online-Shops – Cyber-Betriebsunterbrechung als Baustein der Cyber-Police." },
          { title: "Ausfall wichtiger Personen", text: "Bei einigen Tarifen auch Ausfall des Inhabers/Schlüsselpersons absicherbar." },
        ],
      },
    ],
    notCovered: [
      "Unterbrechungen ohne versicherten Sachschaden (z. B. Pandemie ohne entsprechende Klausel)",
      "Schäden durch unterlassene Wartung",
      "Vorsätzliche Herbeiführung",
      "Mehrkosten ohne wirtschaftlichen Nutzen",
    ],
    conclusion:
      "Wir prüfen Deine Fixkosten und Gewinnsituation und finden die passende Haftzeit und Versicherungssumme – allein oder gebündelt mit Sach-, Maschinen- und Cyber-Policen.",
  }),
];

// Merge internal-linking sets from serviceLinks.ts into each SERVICES entry.
// Kept out-of-line so services.ts stays focused on service content.
import { SERVICE_LINKS } from "./serviceLinks";
for (const s of SERVICES) {
  const links = SERVICE_LINKS[s.slug];
  if (!links) continue;
  if (links.glossaryLinks?.length) s.glossaryLinks = links.glossaryLinks;
  if (links.blogLinks?.length) s.blogLinks = links.blogLinks;
}

export function getServiceBySlug(slug: string): Service | undefined {
  return SERVICES.find((s) => s.slug === slug);
}

export function getServicesByCategory(category: ServiceCategoryName): Service[] {
  return SERVICES.filter((s) => s.category === category);
}
